import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { format, startOfYear, endOfYear, eachDayOfInterval, subMonths, addMonths, startOfDay, subYears, startOfMonth, endOfMonth, getDay } from "date-fns";
import React, { useState, useEffect, useMemo } from "react";
import { useChartViewModeStore, type ChartViewMode } from "@/stores/chartViewModeStore";
import { Button } from "./ui/button";
import { Plus, Trash2, ChevronLeft, ChevronRight, Pencil, Settings2, Palette, X, Hand, ArrowUp, ArrowDown, TrendingUp, Mountain, Grid2X2, Camera, LayoutList, LayoutGrid, CalendarDays, Dices, Grip } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import { useNavigate } from "react-router-dom";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "./ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Checkbox } from "./ui/checkbox";
import { toast } from "sonner";
import { GraphReactions } from "@/components/GraphReactions";
import { 
  getAvailableUnits, 
  convertFromBase, 
  formatValueWithUnit,
  getMetricBaseUnit
} from "@/lib/unitConversions";
import { getUnitPreference } from "@/lib/unitPreferences";
import { getAllMetrics } from "@/lib/metricDefinitions";
import { ProgressSnapViewer } from "@/components/ProgressSnapViewer";
import { DailyEntriesModal } from "@/components/DailyEntriesModal";
import { getLocalizedMetricName } from "@/lib/utils";
import { generateRandomDataWithValues, DEMO_ACCOUNT_USER_ID } from "@/lib/screenshotDataGenerator";

// Add streak aura CSS styles
const streakAuraStyles = `
  @keyframes wispy-flow {
    0% {
      opacity: 0.2;
      filter: blur(8px);
      transform: translateX(0) scale(0.9);
    }
    50% {
      opacity: 0.45;
      filter: blur(14px);
      transform: translateX(-8px) scale(1);
    }
    100% {
      opacity: 0.2;
      filter: blur(8px);
      transform: translateX(0) scale(0.9);
    }
  }

  @keyframes wave-float {
    0%, 100% {
      transform: translateY(0px);
    }
    50% {
      transform: translateY(-1px);
    }
  }

  .streak-aura {
    position: absolute;
    top: -8px;
    bottom: -8px;
    right: -12px;
    left: 50%;
    border-radius: 12px;
    pointer-events: none;
    z-index: 0;
    animation: wispy-flow 1.5s ease-in-out infinite;
  }

  .streak-aura::before {
    content: '';
    position: absolute;
    inset: -4px;
    border-radius: 12px;
    opacity: 0.15;
    filter: blur(6px);
  }

  .streak-wave-square {
    animation: wave-float 2s ease-in-out infinite;
  }

  @keyframes today-pulse-light {
    0%, 100% {
      box-shadow: 0 0 3px 1px rgba(80, 80, 80, 0.25);
    }
    50% {
      box-shadow: 0 0 6px 2px rgba(80, 80, 80, 0.45);
    }
  }

  @keyframes today-pulse-dark {
    0%, 100% {
      box-shadow: 0 0 3px 1px rgba(255, 255, 255, 0.3);
    }
    50% {
      box-shadow: 0 0 6px 2px rgba(255, 255, 255, 0.55);
    }
  }

  .today-glow {
    animation: today-pulse-light 2s ease-in-out infinite;
  }

  .dark .today-glow {
    animation: today-pulse-dark 2s ease-in-out infinite;
  }
`;

interface DailyEntry {
  entry_date: string;
  calories: number | null;
  fiber: number | null;
  added_sugars: number | null;
  protein: number | null;
  miles_ran: number | null;
  sleep: number | null;
  gym_time: number | null;
  carbs: number | null;
  mood: number | null;
  self_appearance_rating: number | null;
  body_weight: number | null;
  body_fat_percentage: number | null;
  water_gallons: number | null;
  ate_healthy: boolean | null;
  low_sugars: boolean | null;
  fasted: boolean | null;
  github_commits: number | null;
  healthkit_steps: number | null;
  healthkit_exercise_time: number | null;
  healthkit_sleep_time: number | null;
  healthkit_stand_hours: number | null;
  healthkit_active_energy: number | null;
  custom_metric_value: number | null;
  custom_metric_emoji: string | null;
  custom_metric_unit: string | null;
  custom_metric_name: string | null;
  custom_1_value: number | null;
  custom_2_value: number | null;
  custom_3_value: number | null;
  custom_4_value: number | null;
  custom_5_value: number | null;
}

interface CustomMetric {
  id: string;
  user_id: string;
  metric_key: string;
  metric_name: string;
  metric_emoji: string;
  metric_unit: string;
  is_inverse?: boolean;
  metric_type?: "numeric" | "boolean";
  is_relative?: boolean; // When true, uses min/max coloring instead of goal-based
}

interface UserGoals {
  calories_goal: number;
  fiber_goal: number;
  added_sugars_goal: number;
  protein_goal: number;
  carbs_goal: number;
  miles_goal: number;
  gym_time_goal: number;
  sleep_goal: number;
  water_goal: number;
  body_weight_goal: number;
  body_fat_goal: number;
  github_commits_goal: number;
  healthkit_steps_goal: number;
  healthkit_exercise_time_goal: number;
  healthkit_sleep_goal: number;
  healthkit_stand_hours_goal: number;
  healthkit_active_energy_goal: number;
  custom_metric_goal: number;
  custom_1_goal?: number;
  custom_2_goal?: number;
  custom_3_goal?: number;
  custom_4_goal?: number;
  custom_5_goal?: number;
  custom_6_goal?: number;
  custom_7_goal?: number;
  custom_8_goal?: number;
  custom_9_goal?: number;
  custom_10_goal?: number;
}

interface ChartPreferences {
  active_metrics: string[];
  available_metrics: string[];
  metric_colors?: Record<string, Record<string, string>>;
}

const getIntensity = (value: number | null, goal: number): number => {
  if (!value) return 0;
  const percentage = (value / goal) * 100;
  if (percentage >= 100) return 4;
  if (percentage >= 75) return 3;
  if (percentage >= 50) return 2;
  if (percentage > 0) return 1;
  return 0;
};

// Special intensity calculation for calories - returns negative values when over/under goal based on isInverse
// isInverse = true (cutting): going OVER goal is bad (red)
// isInverse = false (bulking): no penalty, just normal green shading based on progress
const getCalorieIntensity = (value: number | null, goal: number, isInverse: boolean = true): number => {
  if (!value) return 0;
  const percentage = (value / goal) * 100;
  
  if (isInverse) {
    // Cutting mode: over goal is bad (red)
    if (percentage > 100) {
      const overshoot = percentage - 100;
      if (overshoot >= 50) return -4; // 50%+ over = darkest red
      if (overshoot >= 25) return -3; // 25-50% over = dark red
      if (overshoot >= 10) return -2; // 10-25% over = medium red
      return -1; // 0-10% over = light red
    }
    // Under goal - green
    if (percentage >= 100) return 4;
    if (percentage >= 75) return 3;
    if (percentage >= 50) return 2;
    if (percentage > 0) return 1;
    return 0;
  } else {
    // Bulking mode: no red, just normal green shading based on progress
    // Over goal is great, exactly at goal is great, under goal is still progress
    if (percentage >= 100) return 4; // At or over goal = full green
    if (percentage >= 75) return 3;
    if (percentage >= 50) return 2;
    if (percentage > 0) return 1;
    return 0;
  }
};

// Boolean intensity calculation
// For normal boolean: 0 = gray (0), 1 = green (4)
// For inverse boolean: 0 = gray (0), 1 = red (-4)
const getBooleanIntensity = (value: number | null, isInverse: boolean): number => {
  if (!value || value === 0) return 0;
  return isInverse ? -4 : 4;
};

const getProximityIntensity = (value: number | null, goal: number): number => {
  if (!value) return 0;
  const distance = Math.abs(value - goal);
  const percentageOff = (distance / goal) * 100;

  // On target or very close (within 5%)
  if (percentageOff <= 5) return 4;
  // Close (5-15% off)
  if (percentageOff <= 15) return 3;
  // Moderate (15-30% off)
  if (percentageOff <= 30) return 2;
  // Far (30-50% off)
  if (percentageOff <= 50) return 1;
  // Very far (>50% off)
  return 0;
};

const getBodyFatIntensity = (value: number | null, goal: number): number => {
  if (!value) return 0;
  // Distance in absolute percentage points
  const distance = Math.abs(value - goal);

  // <1% difference - best (on target)
  if (distance < 1) return 4;
  // 1-3% difference - good
  if (distance <= 3) return 3;
  // 3-6% difference - moderate
  if (distance <= 6) return 2;
  // 6-10% difference - far
  if (distance <= 10) return 1;
  // >10% difference - worst
  return 0;
};

// Relative intensity calculation - uses min/max from all entries instead of goal
// For metrics where users "watch what happens" rather than chase a specific goal
// Examples: revenue, temperature, ideas generated, words written
const getRelativeIntensity = (value: number | null, entries: DailyEntry[], metricKey: string): number => {
  if (!value || value === 0) return 0;
  
  // Collect all non-null values for this metric
  const values: number[] = [];
  entries.forEach(entry => {
    let entryValue: number | null = null;
    
    // Extract value based on metric key
    if (metricKey.startsWith('custom_')) {
      const customNum = metricKey.replace('custom_', '');
      entryValue = entry[`custom_${customNum}_value` as keyof DailyEntry] as number | null;
    } else {
      entryValue = entry[metricKey as keyof DailyEntry] as number | null;
    }
    
    if (entryValue !== null && entryValue > 0) {
      values.push(entryValue);
    }
  });
  
  // Need at least 2 values to create a range
  if (values.length < 2) return 4; // If only one value, show it as max intensity
  
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min;
  
  // If all values are the same, show full intensity
  if (range === 0) return 4;
  
  // Calculate position in range (0-1)
  const position = (value - min) / range;
  
  // Map to intensity levels (0-4)
  if (position >= 0.8) return 4;
  if (position >= 0.6) return 3;
  if (position >= 0.4) return 2;
  if (position >= 0.2) return 1;
  return 1; // Always show at least level 1 for any non-zero value
};

// Check if a metric has an active streak (9+ goal-met days in past 14 days)
const hasActiveStreak = (
  metricKey: string,
  entries: DailyEntry[],
  metric: any,
  userGoals: UserGoals,
  customMetrics: CustomMetric[]
): boolean => {
  const today = new Date();
  const fourteenDaysAgo = new Date(today);
  fourteenDaysAgo.setDate(today.getDate() - 14);
  
  // Filter entries in past 14 days
  const recentEntries = entries.filter(entry => {
    const entryDate = new Date(entry.entry_date);
    return entryDate >= fourteenDaysAgo && entryDate <= today;
  });
  
  // Check if metric is inverse (overshoot penalty)
  const isInverse = (metric as any).isInverse || false;
  
  // Don't count streaks for inverse boolean metrics ("yes = bad" habits)
  if ((metric as any).isBoolean && isInverse) {
    return false;
  }
  
  // Count days where goal was met (and not exceeded for overshoot/inverse metrics)
  let goalMetDays = 0;
  
  for (const entry of recentEntries) {
    const intensity = metric.getValue(entry);
    
    // For boolean metrics
    if ((metric as any).isBoolean) {
      // Boolean inverse: goal met when false (intensity = 0)
      // Boolean normal: goal met when true (intensity = 4)
      if (isInverse) {
        if (intensity === 0) goalMetDays++;
      } else {
        if (intensity === 4) goalMetDays++;
      }
    }
    // For calories (special overshoot metric)
    else if (metricKey === 'calories') {
      // Goal met if intensity is positive (4 = perfect, 1-3 = partial, 0 = none)
      // Negative intensity means overshoot
      if (intensity > 0) goalMetDays++;
    }
    // For added_sugars or custom inverse metrics
    else if (metricKey === 'added_sugars' || isInverse) {
      // Goal met if not exceeded (intensity >= 0 and <= 4)
      if (intensity > 0 && intensity <= 4) goalMetDays++;
    }
    // For body_fat_percentage (proximity-based)
    else if (metricKey === 'body_fat_percentage') {
      // Goal met if intensity is high (close to target)
      if (intensity >= 3) goalMetDays++;
    }
    // For normal metrics (higher is better)
    else {
      // Goal met if intensity is 4 (100% or more of goal)
      if (intensity === 4) goalMetDays++;
    }
  }
  
  return goalMetDays >= 9;
};

// Helper function to check if unit is redundant with metric label
const shouldShowUnit = (metricLabel: string, unit: string): boolean => {
  // Check if parameters are valid
  if (!metricLabel || !unit) return true;
  
  // Normalize both to lowercase for case-insensitive comparison
  const normalizedLabel = metricLabel.toLowerCase();
  const normalizedUnit = unit.toLowerCase();
  
  // Check if the unit word (or a close variation) is in the label
  // Handle both plural and singular forms
  const unitVariations = [
    normalizedUnit,
    normalizedUnit.replace(/s$/, ''), // Remove trailing 's' (steps -> step)
    normalizedUnit + 's' // Add 's' (commit -> commits)
  ];
  
  // If any variation of the unit is found in the label, don't show it
  return !unitVariations.some(variation => normalizedLabel.includes(variation));
};

interface ContributionChartProps {
  userId: string;
  filtersOnly?: boolean;
  chartOnly?: boolean;
  showFilters?: boolean;
  isPremium?: boolean;
  onDateSelect?: (date: Date) => void;
  showReactions?: boolean;
  currentViewerId?: string;
  showDiceButtons?: boolean;
}

const ContributionChart = ({

  userId,
  filtersOnly = false,
  chartOnly = false,
  showFilters = false,
  isPremium = false,
  onDateSelect,
  showReactions = false,
  currentViewerId,
  showDiceButtons = false,
}: ContributionChartProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonthOffset, setSelectedMonthOffset] = useState(0); // 0 = current month, -1 = 1 month ago, etc.
  const [activeMetrics, setActiveMetrics] = useState<string[]>([]);
  const [availableMetrics, setAvailableMetrics] = useState<string[]>([]);
  const [editingGoal, setEditingGoal] = useState<{ metricKey: string; currentValue: number; isInverse?: boolean } | null>(null);
  const [metricFiltersDialogOpen, setMetricFiltersDialogOpen] = useState(false);
  const [goalValue, setGoalValue] = useState("");
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [screenWidth, setScreenWidth] = useState<number>(typeof window !== 'undefined' ? window.innerWidth : 768);
  const [localHueValues, setLocalHueValues] = useState<Record<string, number>>({});
  const [localSaturationValues, setLocalSaturationValues] = useState<Record<string, number>>({});
  
  // Chart view mode store (persistent preferences with owner/visitor distinction)
  const { 
    setCurrentProfile, 
    getViewMode, 
    setViewMode: storeSetViewMode 
  } = useChartViewModeStore();
  
  // Subscribe to actual store state to ensure re-renders on preference changes
  const ownerPreferences = useChartViewModeStore((state) => state.ownerPreferences);
  const temporaryPreferences = useChartViewModeStore((state) => state.temporaryPreferences);
  const currentProfileOwner = useChartViewModeStore((state) => state.currentProfileOwner);
  
  // Track if non-premium user has interacted with color picker
  const [nonPremiumColorPickerInteraction, setNonPremiumColorPickerInteraction] = useState(false);
  // Selected progress snap to view
  const [selectedSnap, setSelectedSnap] = useState<any | null>(null);
  // All snaps from the same date for swipe navigation
  const [sameDateSnaps, setSameDateSnaps] = useState<any[]>([]);
  // Condensed view mode for dashboard charts: 'full' | '4weeks' | 'thisMonth' | 'compactGrid'
  type CondensedViewMode = 'full' | '4weeks' | 'thisMonth' | 'compactGrid';
  const [condensedViewMode, setCondensedViewMode] = useState<CondensedViewMode>(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('chartCondensedViewMode');
      if (saved === 'full' || saved === '4weeks' || saved === 'thisMonth' || saved === 'compactGrid') {
        return saved;
      }
    }
    return 'full';
  });
  // Daily entries modal state
  const [dailyEntriesModalOpen, setDailyEntriesModalOpen] = useState(false);
  const [selectedDayForEntries, setSelectedDayForEntries] = useState<{
    date: string;
    metricKey: string;
    metricName: string;
    metricEmoji: string;
    metricUnit: string;
    isBoolean: boolean;
  } | null>(null);
  
  // Screenshot mode: randomized intensity data per metric (for demo account)
  // Persisted to localStorage so it survives page navigation and view changes
  const [randomizedData, setRandomizedData] = useState<Record<string, Map<string, number>>>(() => {
    try {
      const stored = localStorage.getItem('gitfit-screenshot-data');
      if (stored) {
        const parsed = JSON.parse(stored);
        // Convert plain objects back to Maps
        const result: Record<string, Map<string, number>> = {};
        for (const [metricKey, data] of Object.entries(parsed)) {
          result[metricKey] = new Map(Object.entries(data as Record<string, number>));
        }
        return result;
      }
    } catch (e) {
      console.error('Failed to load screenshot data from localStorage:', e);
    }
    return {};
  });

  // Screenshot mode: randomized raw values per metric (for Track page display)
  const [randomizedValues, setRandomizedValues] = useState<Record<string, Map<string, number | boolean | null>>>(() => {
    try {
      const stored = localStorage.getItem('gitfit-screenshot-values');
      if (stored) {
        const parsed = JSON.parse(stored);
        const result: Record<string, Map<string, number | boolean | null>> = {};
        for (const [metricKey, data] of Object.entries(parsed)) {
          result[metricKey] = new Map(Object.entries(data as Record<string, number | boolean | null>));
        }
        return result;
      }
    } catch (e) {
      console.error('Failed to load screenshot values from localStorage:', e);
    }
    return {};
  });

  // Save randomizedData to localStorage whenever it changes
  useEffect(() => {
    if (Object.keys(randomizedData).length > 0) {
      try {
        // Convert Maps to plain objects for JSON serialization
        const toStore: Record<string, Record<string, number>> = {};
        for (const [metricKey, map] of Object.entries(randomizedData)) {
          toStore[metricKey] = Object.fromEntries(map);
        }
        localStorage.setItem('gitfit-screenshot-data', JSON.stringify(toStore));
      } catch (e) {
        console.error('Failed to save screenshot data to localStorage:', e);
      }
    }
  }, [randomizedData]);

  // Save randomizedValues to localStorage whenever it changes
  useEffect(() => {
    if (Object.keys(randomizedValues).length > 0) {
      try {
        const toStore: Record<string, Record<string, number | boolean | null>> = {};
        for (const [metricKey, map] of Object.entries(randomizedValues)) {
          toStore[metricKey] = Object.fromEntries(map);
        }
        localStorage.setItem('gitfit-screenshot-values', JSON.stringify(toStore));
      } catch (e) {
        console.error('Failed to save screenshot values to localStorage:', e);
      }
    }
  }, [randomizedValues]);


  // Track screen width for dynamic column calculation
  // Force updates more aggressively to handle app restart scenarios
  useEffect(() => {
    const handleResize = () => {
      const newWidth = window.innerWidth;
      console.log('[ContributionChart] Screen width changed:', screenWidth, '->', newWidth);
      setScreenWidth(newWidth);
    };
    
    // Check immediately on mount
    handleResize();
    
    window.addEventListener('resize', handleResize);
    
    // Also check on visibility change (app coming back from background)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        console.log('[ContributionChart] App became visible, rechecking screen width');
        handleResize();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      window.removeEventListener('resize', handleResize);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []); // Only run once on mount
  
  // Log mobile state and screen width for debugging layout issues
  useEffect(() => {
    console.log('[ContributionChart] Layout state - isMobile:', isMobile, 'screenWidth:', screenWidth, 'breakpoint: 768px');
  }, [isMobile, screenWidth]);

  // Calculate weeks to show based on screen width (only on mobile)
  // Base: 365px = 15 weeks, +7 days (1 column) per 15px above 365px
  const calculateWeeksToShow = (): number => {
    if (!isMobile) return 52; // Desktop shows full year
    
    const baseWidth = 365;
    const baseWeeks = 15;
    
    if (screenWidth <= baseWidth) return baseWeeks;
    
    const extraPixels = screenWidth - baseWidth;
    const extraWeeks = Math.floor(extraPixels / 15);
    
    return baseWeeks + extraWeeks;
  };

  const weeksToShow = calculateWeeksToShow();

  // Check if viewing own profile and initialize chart view mode store
  useEffect(() => {
    supabase.auth.getUser().then(({ data: { user } }) => {
      const viewerId = user?.id || null;
      setCurrentUserId(viewerId);
      
      // Initialize the chart view mode store with profile owner and viewer
      setCurrentProfile(userId, viewerId);
    });
  }, [userId, setCurrentProfile]);

  const isOwnProfile = currentUserId === userId;

  // Listen for tutorial daily entries modal trigger
  useEffect(() => {
    const handleTutorialDailyEntries = (e: Event) => {
      const customEvent = e as CustomEvent;
      console.log('[ContributionChart] Received open-tutorial-daily-entries event:', customEvent.detail);
      setSelectedDayForEntries(customEvent.detail);
      setDailyEntriesModalOpen(true);
    };

    window.addEventListener('open-tutorial-daily-entries', handleTutorialDailyEntries);
    return () => {
      window.removeEventListener('open-tutorial-daily-entries', handleTutorialDailyEntries);
    };
  }, []);

  // Fetch GitHub connection data (publicly visible)
  const { data: githubConnection } = useQuery({
    queryKey: ["github-connection", userId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("github_connections")
        .select("last_synced_at, github_username")
        .eq("user_id", userId)
        .maybeSingle();
      
      if (error && error.code !== "PGRST116") throw error;
      return data;
    },
  });

  // Load custom metrics
  const { data: customMetrics = [], isLoading: isLoadingCustomMetrics } = useQuery({
    queryKey: ["custom-metrics", userId, currentViewerId],
    queryFn: async () => {
      let data, error;
      if (!currentViewerId) {
        // Anonymous user: use secure RPC that checks is_public
        ({ data, error } = await supabase.rpc("get_public_custom_metrics", {
          target_user_id: userId,
        }));
      } else {
        // Authenticated user: direct query (RLS handles access)
        ({ data, error } = await supabase
          .from("custom_metrics")
          .select("*")
          .eq("user_id", userId)
          .order("metric_key"));
      }
      if (error) throw error;
      const list = (data as CustomMetric[]) || [];
      // Ensure correct numeric ordering: custom_1, custom_2, ... custom_10
      list.sort((a, b) => {
        const ai = parseInt(a.metric_key.split("_")[1] || "0", 10);
        const bi = parseInt(b.metric_key.split("_")[1] || "0", 10);
        return ai - bi;
      });
      return list;
    },
  });

  // Fetch metric preferences to determine which metrics should show graphs
  const { data: quickEditMetrics } = useQuery({
    queryKey: ["metric-preferences", userId, currentViewerId],
    queryFn: async () => {
      if (!currentViewerId) {
        // Anonymous user: use secure RPC that checks is_public
        const { data } = await supabase.rpc("get_public_metric_preferences", {
          target_user_id: userId,
        });
        // RPC returns an array; take the first result
        return Array.isArray(data) ? data[0] || null : data;
      }
      const { data } = await supabase.from("metric_preferences").select("*").eq("user_id", userId).maybeSingle();
      return data;
    },
  });

  // Load saved metric order from user_chart_preferences
  const { data: savedMetricOrder } = useQuery({
    queryKey: ["chart-metric-order", userId, currentViewerId],
    queryFn: async () => {
      if (!currentViewerId) {
        // Anonymous user: use secure RPC that checks is_public
        const { data, error } = await supabase.rpc("get_public_user_chart_preferences", {
          target_user_id: userId,
        });
        if (error && error.code !== "PGRST116") throw error;
        const row = Array.isArray(data) ? data[0] : data;
        return row?.active_metrics || null;
      }
      const { data, error } = await supabase
        .from("user_chart_preferences")
        .select("active_metrics")
        .eq("user_id", userId)
        .maybeSingle();
      
      if (error && error.code !== "PGRST116") throw error;
      return data?.active_metrics || null;
    },
  });

  const hasQuickEditMetrics =
    quickEditMetrics &&
    ((quickEditMetrics.nutrition_metrics && quickEditMetrics.nutrition_metrics.length > 0) ||
      (quickEditMetrics.activity_sleep_metrics && quickEditMetrics.activity_sleep_metrics.length > 0) ||
      (quickEditMetrics.results_metrics && quickEditMetrics.results_metrics.length > 0));

  // Apply metric filters before rendering - now based on QuickMetricButtons preferences
  useEffect(() => {
    if (quickEditMetrics || customMetrics) {
      const normalize = (k: string) =>
        ({
          appearance: "self_appearance_rating",
          miles: "miles_ran",
          water: "water_gallons",
          body_fat: "body_fat_percentage",
        })[k] || k;

      // Build active metrics from QuickMetricButtons preferences
      const activeFromQuickEdit: string[] = [
        ...(quickEditMetrics?.nutrition_metrics || []),
        ...(quickEditMetrics?.activity_sleep_metrics || []),
        ...(quickEditMetrics?.results_metrics || []),
        ...(customMetrics?.map(m => m.metric_key) || [])
      ].map(normalize);

      // Build all available metrics (built-in + custom)
      const allBuiltInMetrics = [
        "calories",
        "protein",
        "carbs",  
        "fiber",
        "added_sugars",
        "water_gallons",
        "miles_ran",
        "gym_time",
        "sleep",
        "body_weight",
        "body_fat_percentage",
        "mood",
        "self_appearance_rating",
        "ate_healthy",
        "low_sugars",
        "fasted",
        "github_commits",
        "healthkit_steps",
        "healthkit_exercise_time",
        "healthkit_sleep_time",
        "healthkit_stand_hours",
        "healthkit_active_energy",
      ];

      const availableFromQuickEdit: string[] = [
        ...allBuiltInMetrics,
        ...(customMetrics?.map(m => m.metric_key) || [])
      ];

      // If there's a saved order, apply it; otherwise use the default order
      let orderedActiveMetrics = activeFromQuickEdit;
      if (savedMetricOrder && Array.isArray(savedMetricOrder) && savedMetricOrder.length > 0) {
        // Filter saved order to only include metrics that are currently active
        const validSavedMetrics = savedMetricOrder.filter(m => activeFromQuickEdit.includes(m));
        // Add any new metrics that weren't in the saved order
        const newMetrics = activeFromQuickEdit.filter(m => !savedMetricOrder.includes(m));
        orderedActiveMetrics = [...validSavedMetrics, ...newMetrics];
      }

      // Update state synchronously to prevent flash
      setActiveMetrics(orderedActiveMetrics);
      setAvailableMetrics(availableFromQuickEdit);
    }
  }, [quickEditMetrics, customMetrics, savedMetricOrder]);

  // Color preferences will be stored separately for premium users
  const { data: colorPreferences } = useQuery({
    queryKey: ["chart-color-preferences", userId, currentViewerId],
    queryFn: async () => {
      if (!currentViewerId) {
        // Anonymous user: use secure RPC that checks is_public
        const { data, error } = await supabase.rpc("get_public_user_chart_preferences", {
          target_user_id: userId,
        });
        if (error && error.code !== "PGRST116") throw error;
        const row = Array.isArray(data) ? data[0] : data;
        return row?.metric_colors || {};
      }
      const { data, error } = await supabase
        .from("user_chart_preferences")
        .select("metric_colors")
        .eq("user_id", userId)
        .maybeSingle();
      
      if (error && error.code !== "PGRST116") throw error;
      return data?.metric_colors || {};
    },
  });

  const updateColorPreferences = useMutation({
    mutationFn: async ({ colors }: { colors: Record<string, Record<string, string>> }) => {
      const { error } = await supabase.from("user_chart_preferences").upsert({
        user_id: userId,
        metric_colors: colors,
      });

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["chart-color-preferences", userId] });
    },
    onError: () => {
      toast.error(t('notifications.toast.failed_to_save_color_preferences'));
    },
  });

  const updateMetricOrder = useMutation({
    mutationFn: async ({ metrics }: { metrics: string[] }) => {
      const { error } = await supabase.from("user_chart_preferences").upsert({
        user_id: userId,
        active_metrics: metrics,
      });

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["chart-metric-order", userId] });
    },
    onError: () => {
      toast.error(t('notifications.toast.failed_to_save_chart_order'));
    },
  });

  const { data: goals } = useQuery({
    queryKey: ["user-goals", userId, currentViewerId],
    queryFn: async () => {
      if (!currentViewerId) {
        // Anonymous user: use secure RPC that checks is_public
        const { data, error } = await supabase.rpc("get_public_user_goals", {
          target_user_id: userId,
        });
        if (error) throw error;
        const row = Array.isArray(data) ? data[0] : data;
        return (row as UserGoals) || null;
      }
      const { data, error } = await supabase.from("user_goals").select("*").eq("user_id", userId).maybeSingle();

      if (error) throw error;
      return data as UserGoals | null;
    },
  });

  // Load default goals from database
  const { data: dbDefaultGoals = {} } = useQuery({
    queryKey: ["default-goals-from-db"],
    queryFn: async () => {
      const metrics = await getAllMetrics();
      const defaults: any = {};
      for (const metric of metrics) {
        if (metric.goalKey && metric.defaultGoal !== undefined) {
          defaults[metric.goalKey] = metric.defaultGoal;
        }
      }
      return defaults;
    },
  });

  const defaultGoals: UserGoals = {
    ...dbDefaultGoals,
    custom_metric_goal: 100,
  } as UserGoals;

  const userGoals = goals || defaultGoals;

  // Determine date range based on device type
  const today = startOfDay(new Date());
  let fetchStartDate: string;
  let fetchEndDate: string;
  let yearStart: Date;
  let yearEnd: Date;

  if (isMobile) {
    // On mobile: dynamically show weeks based on screen width
    // Ensure the chart starts on a Sunday for complete weeks on the left side
    yearEnd = addMonths(today, selectedMonthOffset);
    
    // Calculate start date based on dynamic weeks (weeksToShow * 7 - 1 days back)
    const daysToShow = weeksToShow * 7 - 1;
    const tempStart = new Date(yearEnd);
    tempStart.setDate(yearEnd.getDate() - daysToShow);
    
    // Adjust to the previous Sunday (or stay if already Sunday)
    yearStart = new Date(tempStart);
    yearStart.setDate(tempStart.getDate() - tempStart.getDay()); // Move back to Sunday
    
    fetchStartDate = format(yearStart, "yyyy-MM-dd");
    fetchEndDate = format(yearEnd, "yyyy-MM-dd");
  } else {
    // On desktop: show full year
    const currentYear = today.getFullYear();
    const isCurrentYear = selectedYear === currentYear;
    
    if (isCurrentYear) {
      // For current year: show from 1 year ago to today
      yearEnd = today;
      yearStart = subYears(today, 1);
    } else {
      // For previous years: show full Jan-Dec
      yearStart = startOfYear(new Date(selectedYear, 0, 1));
      yearEnd = endOfYear(new Date(selectedYear, 0, 1));
    }
    
    fetchStartDate = format(yearStart, "yyyy-MM-dd");
    fetchEndDate = format(yearEnd, "yyyy-MM-dd");
  }

  const { data: entries = [], isLoading: isLoadingEntries } = useQuery({
    queryKey: ["daily-entries", userId, currentViewerId, selectedYear, isMobile, selectedMonthOffset, weeksToShow],
    queryFn: async () => {
      let data, error;
      if (!currentViewerId) {
        // Anonymous user: use secure RPC that checks is_public
        ({ data, error } = await supabase.rpc("get_public_daily_entries", {
          target_user_id: userId,
          start_date: fetchStartDate,
          end_date: fetchEndDate,
        }));
      } else {
        // Authenticated user: direct query (RLS handles access)
        ({ data, error } = await supabase
          .from("daily_entries")
          .select("*")
          .eq("user_id", userId)
          .gte("entry_date", fetchStartDate)
          .lte("entry_date", fetchEndDate)
          .order("entry_date", { ascending: true }));
      }
      if (error) throw error;
      return (data as DailyEntry[]) || [];
    },
    enabled: !isLoadingCustomMetrics, // Wait for custom metrics to load before fetching entries
  });

  // Fetch no_data_entries (intentional "no data" markers) for the displayed date range
  const { data: noDataEntriesRaw = [] } = useQuery<Array<{ entry_date: string; metric_key: string }>>({
    queryKey: ["no-data-entries", userId, selectedYear, isMobile, selectedMonthOffset, weeksToShow],
    queryFn: async () => {
      // Only load for own profile (other users don't see these markers)
      if (!currentViewerId || currentViewerId !== userId) return [];
      const { data, error } = await supabase
        .from("no_data_entries" as any)
        .select("entry_date, metric_key")
        .eq("user_id", userId)
        .gte("entry_date", fetchStartDate)
        .lte("entry_date", fetchEndDate);
      if (error) {
        console.error("[ContributionChart] Error fetching no_data_entries:", error);
        return [];
      }
      return (data as unknown as Array<{ entry_date: string; metric_key: string }>) || [];
    },
    enabled: !isLoadingCustomMetrics && !!currentViewerId,
  });

  // Build a Set for O(1) lookups: "YYYY-MM-DD_metricKey"
  const noDataSet = useMemo(() => {
    const set = new Set<string>();
    for (const nd of noDataEntriesRaw) {
      set.add(`${nd.entry_date}_${nd.metric_key}`);
    }
    return set;
  }, [noDataEntriesRaw]);

  // Fetch penalty charges with their associated metric keys
  const { data: penaltyCharges = [] } = useQuery({
    queryKey: ["penalty-charges", userId, selectedYear, isMobile, selectedMonthOffset, weeksToShow],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("penalty_charges")
        .select(
          `
          entry_date,
          penalties!inner(metric_key)
        `,
        )
        .eq("user_id", userId)
        .gte("entry_date", fetchStartDate)
        .lte("entry_date", fetchEndDate);

      if (error) throw error;
      return data || [];
    },
  });

  // Fetch active penalties to check for upcoming charges
  const { data: activePenalties = [] } = useQuery({
    queryKey: ["active-penalties", userId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("penalties")
        .select("*")
        .eq("user_id", userId)
        .eq("active", true)
        .eq("payment_authorized", true);

      if (error) throw error;
      return data || [];
    },
  });

  // Fetch progress snaps for this date range
  const { data: progressSnaps = [] } = useQuery({
    queryKey: ["progress-snaps", userId, currentViewerId, selectedYear, isMobile, selectedMonthOffset, weeksToShow],
    queryFn: async () => {
      // If viewing another user's profile (or not logged in), use the secure function
      // which will return snaps only if the profile is public
      const isOwnProfile = currentViewerId && currentViewerId === userId;
      
      console.log("Fetching snaps - isOwnProfile:", isOwnProfile, "currentViewerId:", currentViewerId, "userId:", userId);
      
      if (!isOwnProfile) {
        // Use secure function for public profile access (anonymous or viewing other user)
        console.log("Using secure function for public snaps");
        const { data, error } = await supabase
          .rpc("get_public_user_snaps", {
            target_user_id: userId,
            start_date: fetchStartDate,
            end_date: fetchEndDate,
          });

        if (error) {
          console.error("Error fetching public snaps:", error);
          return [];
        }
        return data || [];
      } else {
        // Viewing own profile - use direct query
        console.log("Using direct query for own snaps");
        const { data, error } = await supabase
          .from("progress_snaps")
          .select("*")
          .eq("user_id", userId)
          .gte("snap_date", fetchStartDate)
          .lte("snap_date", fetchEndDate);

        if (error) throw error;
        return data || [];
      }
    },
  });

  // Create a map of date -> Set of penalized metric keys
  const penalizedMetrics = new Map<string, Set<string>>();
  if (Array.isArray(penaltyCharges)) {
    penaltyCharges.forEach((charge: any) => {
      const metricKey = charge.penalties?.metric_key;
      if (metricKey) {
        if (!penalizedMetrics.has(charge.entry_date)) {
          penalizedMetrics.set(charge.entry_date, new Set());
        }
        penalizedMetrics.get(charge.entry_date)!.add(metricKey);
      }
    });
  }

  // Create a map of date+metric -> progress snap data
  const progressSnapsByDateMetric = new Map<string, any>();
  if (Array.isArray(progressSnaps)) {
    progressSnaps.forEach((snap: any) => {
      const key = `${snap.snap_date}_${snap.metric_name}`;
      progressSnapsByDateMetric.set(key, snap);
    });
  }

  // Function to check if a date will be charged (within the next 7 days from evaluation)
  const willBeCharged = (dateStr: string, metricKey: string, entry: DailyEntry | undefined): boolean => {
    // Find penalty for this metric
    if (!Array.isArray(activePenalties)) return false;
    const penalty = activePenalties.find((p: any) => p.metric_key === metricKey);
    if (!penalty) return false;

    // Check if this date is within the past 7 days (will be evaluated for penalty)
    const entryDate = new Date(dateStr);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    entryDate.setHours(0, 0, 0, 0);
    const daysAgo = Math.floor((today.getTime() - entryDate.getTime()) / (1000 * 60 * 60 * 24));
    
    // Show warning for entries from 0-6 days ago (will be charged within 7 days)
    if (daysAgo < 0 || daysAgo > 6) return false;

    // Check if penalty was active on that date
    const penaltyCreatedDate = new Date(penalty.created_at).toISOString().split('T')[0];
    if (dateStr < penaltyCreatedDate) return false;

    // Check if this day of week is active for this penalty
    const dayOfWeek = entryDate.getDay();
    const activeWeekdays = penalty.active_weekdays || [0,1,2,3,4,5,6];
    if (!activeWeekdays.includes(dayOfWeek)) return false;

    // Check if entry meets minimum
    const metricValue = entry?.[metricKey as keyof DailyEntry];
    if (!entry || metricValue === null || metricValue === undefined) {
      return true;
    }
    
    // For custom metrics, need to check the _value field
    if (metricKey.startsWith("custom_") && metricKey !== "custom_metric") {
      const match = metricKey.match(/^custom_(\d+)$/);
      if (match) {
        const valueKey = `custom_${match[1]}_value` as keyof DailyEntry;
        const customValue = entry[valueKey];
        if (customValue === null || customValue === undefined) return true;
        return parseFloat(String(customValue)) < parseFloat(String(penalty.minimum_units));
      }
    }
    
    return parseFloat(String(metricValue)) < parseFloat(String(penalty.minimum_units));
  };

  const allDaysInYear = eachDayOfInterval({ start: yearStart, end: yearEnd });

  const days = allDaysInYear.map((date) => {
    const entry = entries.find((e) => e.entry_date === format(date, "yyyy-MM-dd"));
    return {
      date,
      entry,
    };
  });

  // Organize weeks with Sunday at top (day 0) and Saturday at bottom (day 6)
  // Each column should have exactly 7 days
  const weeks: Array<Array<{ date: Date; entry: DailyEntry | undefined } | null>> = [];

  // Find the first Sunday on or before the start date
  const firstDayOfRange = yearStart;
  const firstSunday = new Date(firstDayOfRange);
  firstSunday.setDate(firstDayOfRange.getDate() - firstDayOfRange.getDay());

  // Find the last Saturday on or after the end date
  const lastDayOfRange = yearEnd;
  const lastSaturday = new Date(lastDayOfRange);
  lastSaturday.setDate(lastDayOfRange.getDate() + (6 - lastDayOfRange.getDay()));

  // Generate all days from first Sunday to last Saturday
  const allDays = eachDayOfInterval({ start: firstSunday, end: lastSaturday });

  // Group into weeks of 7 days each
  for (let i = 0; i < allDays.length; i += 7) {
    const week: Array<{ date: Date; entry: DailyEntry | undefined } | null> = [];
    for (let j = 0; j < 7; j++) {
      const currentDate = allDays[i + j];
      if (currentDate) {
        // Check if this date is within the range
        const isInRange = currentDate >= yearStart && currentDate <= yearEnd;
        if (isInRange) {
          const entry = entries.find((e) => e.entry_date === format(currentDate, "yyyy-MM-dd"));
          week.push({ date: currentDate, entry });
        } else {
          // Push null for dates outside the range (will be rendered as empty)
          week.push(null);
        }
      }
    }
    if (week.length > 0) {
      weeks.push(week);
    }
  }

  // Get month labels for each week
  const getMonthLabel = (weekIndex: number): string | null => {
    if (weeks[weekIndex]) {
      // Find the first non-null day in the week
      const firstDay = weeks[weekIndex].find((day) => day !== null);
      if (firstDay && firstDay.date) {
        const currentMonth = firstDay.date.getMonth();
        // Check if this is the first week showing this month
        // by checking if the previous week was a different month or doesn't exist
        if (weekIndex === 0) {
          // For mobile, always show the first month label if there's space
          if (isMobile) {
            // Find the next week with a different month
            let nextMonthWeekIndex = -1;
            for (let i = weekIndex + 1; i < weeks.length; i++) {
              const checkDay = weeks[i]?.find((day) => day !== null);
              if (checkDay && checkDay.date.getMonth() !== currentMonth) {
                nextMonthWeekIndex = i;
                break;
              }
            }
            // Only show first label if there's at least 3 weeks before next month change
            if (nextMonthWeekIndex === -1 || nextMonthWeekIndex - weekIndex >= 3) {
              return format(firstDay.date, "MMM");
            }
            return null;
          }
          // For desktop, check if next month label is too close
          // Find the next week with a different month
          let nextMonthWeekIndex = -1;
          for (let i = weekIndex + 1; i < weeks.length; i++) {
            const checkDay = weeks[i]?.find((day) => day !== null);
            if (checkDay && checkDay.date.getMonth() !== currentMonth) {
              nextMonthWeekIndex = i;
              break;
            }
          }
          // If next month label is within 4 weeks, skip the first label
          if (nextMonthWeekIndex !== -1 && nextMonthWeekIndex - weekIndex <= 4) {
            return null;
          }
          return format(firstDay.date, "MMM");
        }
        const prevWeek = weeks[weekIndex - 1];
        const prevDay = prevWeek?.find((day) => day !== null);
        if (prevDay && prevDay.date.getMonth() !== currentMonth) {
          return format(firstDay.date, "MMM");
        }
      }
    }
    return null;
  };

  const defaultColors = {
    0: "hsl(var(--muted))",
    1: "color-mix(in srgb, #39d353 25%, hsl(var(--muted)))",
    2: "color-mix(in srgb, #39d353 50%, hsl(var(--muted)))",
    3: "color-mix(in srgb, #39d353 75%, hsl(var(--muted)))",
    4: "#39d353",
  };

  // Red color palette for calories overshoot (negative intensity values)
  const redColors = {
    "-1": "color-mix(in srgb, #ef4444 25%, hsl(var(--muted)))", // light red (0-10% over)
    "-2": "color-mix(in srgb, #ef4444 50%, hsl(var(--muted)))", // medium red (10-25% over)
    "-3": "color-mix(in srgb, #ef4444 75%, hsl(var(--muted)))", // dark red (25-50% over)
    "-4": "#ef4444", // full red (50%+ over)
  };

  // Red to Green gradient preset (red → orange → yellow → light green → green)
  // Use string keys to match how they're stored/retrieved from database
  const redToGreenPreset: Record<string, string> = {
    "0": "hsl(var(--muted))",  // Empty/no data
    "1": "#ef4444",            // Red (low/bad)
    "2": "#f97316",            // Orange 
    "3": "#eab308",            // Yellow (medium)
    "4": "#22c55e",            // Green (high/good)
  };

  // Green to Red gradient preset (green → yellow → orange → red) - for inverse metrics
  // Use string keys to match how they're stored/retrieved from database
  const greenToRedPreset: Record<string, string> = {
    "0": "hsl(var(--muted))",  // Empty/no data
    "1": "#22c55e",            // Green (low is good for inverse)
    "2": "#eab308",            // Yellow
    "3": "#f97316",            // Orange 
    "4": "#ef4444",            // Red (high is bad for inverse)
  };

  // Generate color palette from a base color
  const generateColorPalette = (baseColor: string): Record<number, string> => {
    // Parse the hex color to RGB
    const r = parseInt(baseColor.slice(1, 3), 16);
    const g = parseInt(baseColor.slice(3, 5), 16);
    const b = parseInt(baseColor.slice(5, 7), 16);

    // Get muted color values - we'll blend towards this
    const mutedBg = getComputedStyle(document.documentElement).getPropertyValue("--muted").trim();

    // Generate 5 levels that progressively get more saturated (like the default green)
    // Lower values are more muted, higher values are more vibrant
    return {
      0: "hsl(var(--muted))", // Empty/no data
      1: `color-mix(in srgb, ${baseColor} 25%, hsl(var(--muted)))`, // 25% color, mostly muted
      2: `color-mix(in srgb, ${baseColor} 50%, hsl(var(--muted)))`, // 50% color, medium
      3: `color-mix(in srgb, ${baseColor} 75%, hsl(var(--muted)))`, // 75% color, vibrant
      4: baseColor, // Full saturation
    };
  };

  const getSquareColor = (intensity: number, isPenalized: boolean = false, metricKey?: string): string => {
    if (isPenalized) return "bg-red-600";

    // Use custom colors if available for this metric
    const customColors = colorPreferences?.[metricKey || ""];
    if (customColors && customColors[intensity.toString()]) {
      return `bg-[${customColors[intensity.toString()]}]`;
    }

    // Return empty string - we'll use inline styles with defaultColors or redColors
    return "";
  };

  // Generate random data for a metric (screenshot mode)
  const randomizeMetricData = (metricKey: string, goal: number, isInverse: boolean = false, isBoolean: boolean = false) => {
    console.log('[Screenshot] randomizeMetricData called:', { metricKey, goal, isInverse, isBoolean });
    const startDate = new Date('2025-03-01');
    const endDate = new Date('2026-04-30');
    
    const { intensities, values } = generateRandomDataWithValues(
      { key: metricKey, goal, isInverse, isBoolean },
      startDate,
      endDate
    );
    console.log('[Screenshot] Generated intensities sample:', Array.from(intensities.entries()).slice(0, 5));
    
    setRandomizedData(prev => ({
      ...prev,
      [metricKey]: intensities
    }));
    
    setRandomizedValues(prev => ({
      ...prev,
      [metricKey]: values
    }));
  };

  // Check if viewing own profile AND is the demo account (for screenshot mode)
  const isViewingOwnProfile = currentViewerId && currentViewerId === userId;
  const isDemoUser = userId === DEMO_ACCOUNT_USER_ID;

  // Get intensity for a date, using randomized data if available
  // Note: randomized data persists even when dice buttons are hidden
  // Only apply screenshot data when viewing own profile AND is demo account
  const getDisplayIntensity = (metricKey: string, dateStr: string, originalIntensity: number): number => {
    if (isViewingOwnProfile && isDemoUser && randomizedData[metricKey]) {
      return randomizedData[metricKey].get(dateStr) ?? 0;
    }
    return originalIntensity;
  };

  const getCombinedIntensity = (entry: DailyEntry | undefined): number => {
    if (!entry) return 0;

    const intensities = [
      getIntensity(entry.calories, userGoals.calories_goal),
      getIntensity(entry.fiber, userGoals.fiber_goal),
      getIntensity(entry.protein, userGoals.protein_goal),
      getIntensity(entry.carbs, userGoals.carbs_goal),
      getIntensity(entry.miles_ran, userGoals.miles_goal),
      getIntensity(entry.sleep, userGoals.sleep_goal),
    ];

    const avg = intensities.reduce((a, b) => a + b, 0) / intensities.length;
    return Math.round(avg);
  };

  // Get custom metric config (prefer most recent across all time; fallback to latest in current year)
  const { data: recentConfig } = useQuery({
    queryKey: ["recent-entry", userId, currentViewerId],
    queryFn: async () => {
      if (!currentViewerId) {
        // For anonymous users, skip this extra query.
        // The latestEntry fallback from the already-loaded entries handles custom metric config.
        return null;
      }
      const { data, error } = await supabase
        .from("daily_entries")
        .select("custom_metric_emoji, custom_metric_name, custom_metric_unit")
        .eq("user_id", userId)
        .order("entry_date", { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error && (error as any).code !== "PGRST116") throw error;
      return data as Partial<DailyEntry> | null;
    },
  });

  const latestEntry = entries.length > 0 ? entries[entries.length - 1] : null;

  const customMetricEmoji = recentConfig?.custom_metric_emoji || latestEntry?.custom_metric_emoji || "⭐";
  const customMetricName = recentConfig?.custom_metric_name || latestEntry?.custom_metric_name || "Custom";
  const customMetricUnit = recentConfig?.custom_metric_unit || latestEntry?.custom_metric_unit || "units";

  // Build dynamic metrics list including custom metrics
  const allMetrics = [
    {
      key: "calories",
      label: getLocalizedMetricName("calories", t, "Calories"),
      emoji: "🔥",
      goal: userGoals.calories_goal,
      getValue: (entry: DailyEntry | undefined) => getCalorieIntensity(entry?.calories || null, userGoals.calories_goal, userGoals.calories_inverse !== false),
      unit: "cal",
      isInverse: userGoals.calories_inverse !== false,
    },
    {
      key: "fiber",
      label: getLocalizedMetricName("fiber", t, "Fiber"),
      emoji: "🌾",
      goal: userGoals.fiber_goal,
      getValue: (entry: DailyEntry | undefined) => getIntensity(entry?.fiber || null, userGoals.fiber_goal),
      unit: "g",
    },
    {
      key: "added_sugars",
      label: getLocalizedMetricName("added_sugars", t, "Added Sugars"),
      emoji: "🍬",
      goal: userGoals.added_sugars_goal,
      getValue: (entry: DailyEntry | undefined) => getCalorieIntensity(entry?.added_sugars || null, userGoals.added_sugars_goal, true),
      unit: "g",
      isInverse: true,
    },
    {
      key: "protein",
      label: getLocalizedMetricName("protein", t, "Protein"),
      emoji: "🍗",
      goal: userGoals.protein_goal,
      getValue: (entry: DailyEntry | undefined) => getIntensity(entry?.protein || null, userGoals.protein_goal),
      unit: "g",
    },
    {
      key: "carbs",
      label: getLocalizedMetricName("carbs", t, "Carbs"),
      emoji: "🍞",
      goal: userGoals.carbs_goal,
      getValue: (entry: DailyEntry | undefined) => getIntensity(entry?.carbs || null, userGoals.carbs_goal),
      unit: "g",
    },
    {
      key: "miles_ran",
      label: getLocalizedMetricName("miles_ran", t, "Run Distance"),
      emoji: "🏃",
      goal: userGoals.miles_goal,
      getValue: (entry: DailyEntry | undefined) => getIntensity(entry?.miles_ran || null, userGoals.miles_goal),
      unit: "mi",
    },
    {
      key: "gym_time",
      label: getLocalizedMetricName("gym_time", t, "Gym Time"),
      emoji: "💪",
      goal: userGoals.gym_time_goal,
      getValue: (entry: DailyEntry | undefined) => getIntensity(entry?.gym_time || null, userGoals.gym_time_goal),
      unit: "min",
    },
    {
      key: "sleep",
      label: getLocalizedMetricName("sleep", t, "Sleep"),
      emoji: "😴",
      goal: userGoals.sleep_goal,
      getValue: (entry: DailyEntry | undefined) => getIntensity(entry?.sleep || null, userGoals.sleep_goal),
      unit: "hrs",
    },
    {
      key: "water_gallons",
      label: getLocalizedMetricName("water_gallons", t, "Water"),
      emoji: "💧",
      goal: userGoals.water_goal,
      getValue: (entry: DailyEntry | undefined) => getIntensity(entry?.water_gallons || null, userGoals.water_goal),
      unit: "gal",
    },
    {
      key: "github_commits",
      label: getLocalizedMetricName("github_commits", t, "GitHub Commits"),
      emoji: "💻",
      goal: userGoals.github_commits_goal,
      getValue: (entry: DailyEntry | undefined) =>
        getIntensity(entry?.github_commits || null, userGoals.github_commits_goal),
      unit: "commits",
    },
    {
      key: "healthkit_steps",
      label: getLocalizedMetricName("healthkit_steps", t, "Steps"),
      emoji: "👟",
      goal: userGoals.healthkit_steps_goal,
      getValue: (entry: DailyEntry | undefined) =>
        getIntensity(entry?.healthkit_steps || null, userGoals.healthkit_steps_goal),
      unit: "steps",
    },
    {
      key: "healthkit_exercise_time",
      label: getLocalizedMetricName("healthkit_exercise_time", t, "Exercise"),
      emoji: "🏋️",
      goal: userGoals.healthkit_exercise_time_goal,
      getValue: (entry: DailyEntry | undefined) =>
        getIntensity(entry?.healthkit_exercise_time || null, userGoals.healthkit_exercise_time_goal),
      unit: "min",
    },
    {
      key: "healthkit_sleep_time",
      label: getLocalizedMetricName("sleep", t, "Sleep Time"),
      emoji: "😴",
      goal: userGoals.healthkit_sleep_goal,
      getValue: (entry: DailyEntry | undefined) =>
        getIntensity(entry?.healthkit_sleep_time || null, userGoals.healthkit_sleep_goal),
      unit: "hrs",
    },
    {
      key: "healthkit_stand_hours",
      label: getLocalizedMetricName("healthkit_stand_hours", t, "Stand Hours"),
      emoji: "🚶",
      goal: userGoals.healthkit_stand_hours_goal,
      getValue: (entry: DailyEntry | undefined) =>
        getIntensity(entry?.healthkit_stand_hours || null, userGoals.healthkit_stand_hours_goal),
      unit: "hrs",
    },
    {
      key: "healthkit_active_energy",
      label: getLocalizedMetricName("healthkit_active_energy", t, "Active Energy"),
      emoji: "🏃‍♂️",
      goal: userGoals.healthkit_active_energy_goal,
      getValue: (entry: DailyEntry | undefined) =>
        getIntensity(entry?.healthkit_active_energy || null, userGoals.healthkit_active_energy_goal),
      unit: "kcal",
    },
    {
      key: "body_weight",
      label: getLocalizedMetricName("body_weight", t, "Weight"),
      emoji: "⚖️",
      goal: userGoals.body_weight_goal,
      getValue: (entry: DailyEntry | undefined) => getIntensity(entry?.body_weight || null, userGoals.body_weight_goal),
      unit: "lbs",
    },
    {
      key: "body_fat_percentage",
      label: getLocalizedMetricName("body_fat_percentage", t, "Body Fat"),
      emoji: "📊",
      goal: userGoals.body_fat_goal,
      getValue: (entry: DailyEntry | undefined) =>
        getBodyFatIntensity(entry?.body_fat_percentage || null, userGoals.body_fat_goal),
      unit: "%",
    },
    {
      key: "ate_healthy",
      label: getLocalizedMetricName("ate_healthy", t, "Ate Healthy"),
      emoji: "🥗",
      goal: 1,
      getValue: (entry: DailyEntry | undefined) => (entry?.ate_healthy ? 4 : 0),
      unit: "",
    },
    {
      key: "low_sugars",
      label: getLocalizedMetricName("low_sugars", t, "Low Sugars"),
      emoji: "🚫",
      goal: 1,
      getValue: (entry: DailyEntry | undefined) => (entry?.low_sugars ? 4 : 0),
      unit: "",
    },
    {
      key: "fasted",
      label: getLocalizedMetricName("fasted", t, "Fasted"),
      emoji: "🍽️",
      goal: 1,
      getValue: (entry: DailyEntry | undefined) => (entry?.fasted ? 4 : 0),
      unit: "",
    },
    {
      key: "mood",
      label: getLocalizedMetricName("mood", t, "Mood"),
      emoji: "😊",
      goal: 10,
      // Map mood (1-10) to intensity (0-4): 1-2→1, 3-4→2, 5-6→3, 7-10→4
      getValue: (entry: DailyEntry | undefined) => {
        const mood = entry?.mood;
        if (!mood) return 0;
        if (mood <= 2) return 1;
        if (mood <= 4) return 2;
        if (mood <= 6) return 3;
        return 4;
      },
      unit: "rating",
    },
    {
      key: "self_appearance_rating",
      label: getLocalizedMetricName("self_appearance_rating", t, "Self Appearance"),
      emoji: "✨",
      goal: 10,
      // Map rating (1-10) to intensity (0-4): 1-2→1, 3-4→2, 5-6→3, 7-10→4
      getValue: (entry: DailyEntry | undefined) => {
        const rating = entry?.self_appearance_rating;
        if (!rating) return 0;
        if (rating <= 2) return 1;
        if (rating <= 4) return 2;
        if (rating <= 6) return 3;
        return 4;
      },
      unit: "rating",
    },
    {
      key: "custom_metric",
      label: customMetricName,
      emoji: customMetricEmoji,
      goal: userGoals.custom_metric_goal,
      getValue: (entry: DailyEntry | undefined) =>
        getIntensity(entry?.custom_metric_value || null, userGoals.custom_metric_goal),
      unit: customMetricUnit,
    },
    ...customMetrics.map((metric, index) => {
      // Extract the slot number from metric_key (e.g., "custom_6" -> "6")
      const slotMatch = metric.metric_key.match(/^custom_(\d+)$/);
      const slotNumber = slotMatch ? slotMatch[1] : (index + 1).toString();
      const goalKey = `custom_${slotNumber}_goal` as keyof UserGoals;
      const valueKey = `custom_${slotNumber}_value`;
      // NEVER retrieve goal for relative metrics - they use min/max coloring instead
      const goal = metric.is_relative ? undefined : ((userGoals as any)[goalKey] || 100);
      
      // Boolean metrics use getBooleanIntensity
      if (metric.metric_type === "boolean") {
        return {
          key: metric.metric_key,
          label: metric.metric_name,
          emoji: metric.metric_emoji,
          goal: 1, // Boolean has no numeric goal
          getValue: (entry: DailyEntry | undefined) =>
            getBooleanIntensity((entry as any)?.[valueKey] || null, metric.is_inverse || false),
          unit: "",
          isInverse: metric.is_inverse || false,
          isBoolean: true,
        };
      }
      
      // Relative metrics use getRelativeIntensity (no goal needed)
      if (metric.is_relative) {
        return {
          key: metric.metric_key,
          label: metric.metric_name,
          emoji: metric.metric_emoji,
          goal: undefined, // No goal for relative metrics
          getValue: (entry: DailyEntry | undefined) =>
            getRelativeIntensity((entry as any)?.[valueKey] || null, entries, valueKey),
          unit: metric.metric_unit,
          isInverse: false,
          isBoolean: false,
          isRelative: true,
        };
      }
      
      // Numeric metrics: use getCalorieIntensity for inverse, getIntensity for normal
      const intensityFn = metric.is_inverse 
        ? (val: number | null, g: number) => getCalorieIntensity(val, g, true)
        : getIntensity;
      
      return {
        key: metric.metric_key,
        label: metric.metric_name,
        emoji: metric.metric_emoji,
        goal,
        getValue: (entry: DailyEntry | undefined) =>
          intensityFn((entry as any)?.[valueKey] || null, goal),
        unit: metric.metric_unit,
        isInverse: metric.is_inverse || false,
        isBoolean: false,
      };
    }),
  ];

  const normalizeKey = (k: string) =>
    ({
      appearance: "self_appearance_rating",
      miles: "miles_ran",
      water: "water_gallons",
      body_fat: "body_fat_percentage",
    })[k] || k;

  const openQuickEditForMetric = (metricKey: string) => {
    if (isMobile) {
      // On mobile, navigate to /track with the metric in state
      navigate("/track", { state: { openMetric: metricKey } });
    } else {
      // On desktop, dispatch event to open the dialog
      window.dispatchEvent(new CustomEvent("open-quick-edit", { detail: { metricKey } }));
    }
  };

  const moveChart = (index: number, direction: "up" | "down") => {
    const newIndex = direction === "up" ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= activeMetrics.length) return;

    const newActiveMetrics = [...activeMetrics];
    const [movedMetric] = newActiveMetrics.splice(index, 1);
    newActiveMetrics.splice(newIndex, 0, movedMetric);
    
    setActiveMetrics(newActiveMetrics);
    
    // Save to database
    updateMetricOrder.mutate({ metrics: newActiveMetrics });
  };

  // Cycle through view modes: bar -> line-cumulative -> line-rolling -> bar
  const toggleViewMode = (metricKey: string) => {
    const currentMode = getViewMode(metricKey) || 'bar';
    const nextMode: ChartViewMode = currentMode === 'bar' ? 'line-cumulative' : 
                    currentMode === 'line-cumulative' ? 'line-rolling' : 
                    'bar';
    storeSetViewMode(metricKey, nextMode);
  };

  // Get icon for current view mode - uses subscribed state for proper reactivity
  const getViewModeIcon = (metricKey: string) => {
    // Compute mode directly from subscribed state
    const key = currentProfileOwner ? `${currentProfileOwner}:${metricKey}` : null;
    const mode = key 
      ? (temporaryPreferences[key] || ownerPreferences[key] || 'bar')
      : 'bar';
    switch (mode) {
      case 'bar':
        return Grid2X2;
      case 'line-cumulative':
        return TrendingUp;
      case 'line-rolling':
        return Mountain;
      default:
        return Grid2X2; // Fallback to bar mode icon
    }
  };

  // Helper to check if viewing in bar/squares mode - uses subscribed state for proper reactivity
  const isBarMode = (metricKey: string) => {
    // Compute mode directly from subscribed state
    const key = currentProfileOwner ? `${currentProfileOwner}:${metricKey}` : null;
    const mode = key 
      ? (temporaryPreferences[key] || ownerPreferences[key])
      : undefined;
    return mode === 'bar' || !mode;
  };

  // View mode toggle button component
  const ViewModeButton = ({ metricKey }: { metricKey: string }) => (
    <Button
      variant="ghost"
      size="sm"
      className="h-4 w-4 p-0 hover:bg-transparent ml-1"
      onClick={(e) => {
        e.stopPropagation();
        toggleViewMode(metricKey);
      }}
      title={t('accessibility.labels.toggle_view_mode')}
    >
      {React.createElement(getViewModeIcon(metricKey), { 
        className: "w-3 h-3 text-muted-foreground/60 hover:text-muted-foreground" 
      })}
    </Button>
  );

  // Color preset buttons component
  const PresetButtons = ({ metricKey }: { metricKey: string }) => (
    <div className="space-y-2">
      <div className="text-xs text-muted-foreground">{t('ui.text.or_use_preset')}</div>
      <div className="flex flex-col gap-1">
        <Button
          variant={isRedToGreenPreset(metricKey) ? "default" : "outline"}
          size="sm"
          onClick={() => applyRedToGreenPreset(metricKey)}
          className="text-xs h-8 w-full"
        >
          <div className="flex items-center gap-2">
            <div className="flex gap-0.5">
              {[1, 2, 3, 4].map((level) => (
                <div key={level} className="w-3 h-3 rounded-sm" style={{ backgroundColor: redToGreenPreset[level.toString()] }} />
              ))}
            </div>
            <span>{t('ui.text.red_to_green')}</span>
          </div>
        </Button>
        <Button
          variant={isGreenToRedPreset(metricKey) ? "default" : "outline"}
          size="sm"
          onClick={() => applyGreenToRedPreset(metricKey)}
          className="text-xs h-8 w-full"
        >
          <div className="flex items-center gap-2">
            <div className="flex gap-0.5">
              {[1, 2, 3, 4].map((level) => (
                <div key={level} className="w-3 h-3 rounded-sm" style={{ backgroundColor: greenToRedPreset[level.toString()] }} />
              ))}
            </div>
            <span>{t('ui.text.green_to_red')}</span>
          </div>
        </Button>
      </div>
    </div>
  );

  const ScalingButtons = ({ metricKey }: { metricKey: string }) => {
    const currentScaling = getIntensityScaling(metricKey);
    return (
      <div className="space-y-1.5">
        <div className="text-xs text-muted-foreground">{t('ui.text.square_scaling')}</div>
        <div className="flex gap-1">
          {(['none', 'proportional', 'inverse'] as const).map((mode) => (
            <Button
              key={mode}
              variant={currentScaling === mode ? 'default' : 'outline'}
              size="sm"
              onClick={() => setIntensityScaling(metricKey, mode)}
              className="flex-1 text-[10px] px-1 h-7"
            >
              <div className="flex items-center gap-1">
                {mode !== 'none' && (
                  <div className="flex items-end gap-px">
                    {[1, 2, 3, 4].map((level) => {
                      const scale = getSquareScale(level, mode);
                      const size = Math.round(8 * scale);
                      return (
                        <div
                          key={level}
                          className="rounded-[1px] bg-current"
                          style={{ width: `${size}px`, height: `${size}px` }}
                        />
                      );
                    })}
                  </div>
                )}
                <span>{mode === 'none' ? t('ui.text.no_scaling') : mode === 'proportional' ? t('ui.text.scale_up') : t('ui.text.scale_down')}</span>
              </div>
            </Button>
          ))}
        </div>
      </div>
    );
  };

  // Helper to process data for line charts
  const processLineChartData = (weeks: any[][], metric: any, mode: 'line-cumulative' | 'line-rolling') => {
    const dataPoints: { x: number; y: number; date: Date; hasSnap?: boolean; snapCount?: number; snapDates?: string[] }[] = [];
    let cumulativeTotal = 0;
    const rollingWindow: number[] = [];
    
    // Helper to get the actual raw value from an entry
    const getActualValue = (entry: DailyEntry | undefined): number => {
      if (!entry) return 0;
      
      // For boolean metrics, return 1 or 0
      if (metric.key === "ate_healthy") return entry.ate_healthy ? 1 : 0;
      if (metric.key === "low_sugars") return entry.low_sugars ? 1 : 0;
      if (metric.key === "fasted") return entry.fasted ? 1 : 0;
      
      // For custom metrics (custom_1, custom_2, etc.), access the _value field
      if (metric.key.startsWith("custom_") && metric.key !== "custom_metric") {
        const match = metric.key.match(/^custom_(\d+)$/);
        if (match) {
          const valueKey = `custom_${match[1]}_value` as keyof DailyEntry;
          const customValue = entry[valueKey];
          return customValue ? parseFloat(String(customValue)) : 0;
        }
      }
      
      // For numeric metrics, return the actual value
      const value = (entry as any)[metric.key];
      return value || 0;
    };
    
    // Aggregate data by week for smoother lines
    weeks.forEach((week, weekIndex) => {
      let weekTotal = 0;
      let weekCount = 0;
      let lastDayInWeek: any = null;
      let hasSnapInWeek = false;
      let snapDatesInWeek: string[] = []; // Store ALL dates with snaps in this week
      
      week.forEach((day, dayIndex) => {
        if (!day) return;
        
        // Use actual values instead of intensity-based values
        const actualValue = getActualValue(day.entry);
        
        weekTotal += actualValue;
        weekCount++;
        lastDayInWeek = day;
        
        // Check if this day has a snap for this metric
        const dateStr = format(day.date, "yyyy-MM-dd");
        const snapKey = `${dateStr}_${metric.key}`;
        if (progressSnapsByDateMetric.has(snapKey)) {
          hasSnapInWeek = true;
          snapDatesInWeek.push(dateStr);
        }
        
        if (mode === 'line-cumulative') {
          cumulativeTotal += actualValue;
        } else {
          rollingWindow.push(actualValue);
          if (rollingWindow.length > 3) rollingWindow.shift();
        }
      });
      
      // Add one point per week instead of per day for smoother lines
      if (weekCount > 0 && lastDayInWeek) {
        const x = weekIndex * 7 + 3; // Middle of the week
        
        if (mode === 'line-cumulative') {
          dataPoints.push({
            x,
            y: cumulativeTotal,
            date: lastDayInWeek.date,
            hasSnap: hasSnapInWeek,
            snapCount: snapDatesInWeek.length,
            snapDates: snapDatesInWeek // Store ALL dates with snaps in this week
          });
        } else {
          const avg = rollingWindow.reduce((sum, val) => sum + val, 0) / rollingWindow.length;
          dataPoints.push({
            x,
            y: avg,
            date: lastDayInWeek.date,
            hasSnap: hasSnapInWeek,
            snapCount: snapDatesInWeek.length,
            snapDates: snapDatesInWeek // Store ALL dates with snaps in this week
          });
        }
      }
    });
    
    return dataPoints;
  };

  // Helper to get chart color
  const getChartColor = (metricKey: string) => {
    const customColor = colorPreferences?.[metricKey]?.['4'];
    if (customColor) return customColor;
    
    // Default colors based on metric type
    const localHue = localHueValues[metricKey];
    const localSat = localSaturationValues[metricKey];
    if (localHue !== undefined) {
      const sat = localSat ?? 100;
      return `hsl(${localHue}, ${sat}%, ${100 - sat / 2}%)`;
    }
    
    return 'hsl(142, 76%, 36%)'; // Default green
  };

  const handleColorChange = (metricKey: string, hue: number, saturation: number = 100) => {
    // Update local state immediately for responsive UI
    setLocalHueValues((prev) => ({ ...prev, [metricKey]: hue }));
    setLocalSaturationValues((prev) => ({ ...prev, [metricKey]: saturation }));

    // Track that non-premium user has interacted with color picker
    if (!isPremium) {
      setNonPremiumColorPickerInteraction(true);
    }

    // Convert hue+saturation to hex for storage
    // saturation slider: 0 = white, 100 = fully vivid
    const cssSaturation = saturation;
    const cssLightness = 100 - (saturation / 2);
    const hslToHex = (h: number, s: number, l: number) => {
      l /= 100;
      const a = (s * Math.min(l, 1 - l)) / 100;
      const f = (n: number) => {
        const k = (n + h / 30) % 12;
        const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
        return Math.round(255 * color)
          .toString(16)
          .padStart(2, "0");
      };
      return `#${f(0)}${f(8)}${f(4)}`;
    };

    const baseHex = hslToHex(hue, cssSaturation, cssLightness);

    const currentColors = (colorPreferences || {}) as Record<string, Record<string, string>>;
    const generatedPalette = generateColorPalette(baseHex);

    const updatedColors: Record<string, Record<string, string>> = {
      ...currentColors,
      [metricKey]: generatedPalette,
    };

    // Only save to DB if premium user
    if (isPremium) {
      updateColorPreferences.mutate({ colors: updatedColors });
    }
  };

  // Handle color picker popover close
  const handleColorPickerOpenChange = (open: boolean, metricKey: string) => {
    // When popover closes and non-premium user has interacted
    if (!open && !isPremium && nonPremiumColorPickerInteraction) {
      // Reset the interaction flag
      setNonPremiumColorPickerInteraction(false);
      // Clear the local hue/saturation values to revert the preview
      setLocalHueValues((prev) => {
        const updated = { ...prev };
        delete updated[metricKey];
        return updated;
      });
      setLocalSaturationValues((prev) => {
        const updated = { ...prev };
        delete updated[metricKey];
        return updated;
      });
      // Show toast and navigate to upgrade page
      toast.error(t('notifications.toast.custom_chart_colors_are_a_pro_only_feature'));
      navigate("/upgrade");
    }
  };

  // Extract hue from a custom color (for slider default value)
  const getHueFromColor = (colorStr: string): number => {
    // Try to extract hue from hex color
    if (colorStr.startsWith("#")) {
      const hex = colorStr.replace("#", "");
      const r = parseInt(hex.substring(0, 2), 16) / 255;
      const g = parseInt(hex.substring(2, 4), 16) / 255;
      const b = parseInt(hex.substring(4, 6), 16) / 255;

      const max = Math.max(r, g, b);
      const min = Math.min(r, g, b);

      let h = 0;
      if (max !== min) {
        const d = max - min;
        if (max === r) {
          h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
        } else if (max === g) {
          h = ((b - r) / d + 2) / 6;
        } else {
          h = ((r - g) / d + 4) / 6;
        }
      }
      return Math.round(h * 360);
    }
    // Default to green (142 degrees) if can't parse
    return 142;
  };

  // Extract saturation (vibrancy) from a stored hex color
  // Maps back to slider value: 0 = white, 100 = fully vivid
  const getSaturationFromColor = (colorStr: string): number => {
    if (colorStr.startsWith("#")) {
      const hex = colorStr.replace("#", "");
      const r = parseInt(hex.substring(0, 2), 16) / 255;
      const g = parseInt(hex.substring(2, 4), 16) / 255;
      const b = parseInt(hex.substring(4, 6), 16) / 255;

      const max = Math.max(r, g, b);
      const min = Math.min(r, g, b);
      const l = (max + min) / 2;

      if (max === min) return 0; // achromatic (white/gray/black)

      const d = max - min;
      const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

      // Map HSL saturation & lightness back to our slider value
      // Our formula: cssSat = slider, cssLight = 100 - slider/2
      // So slider = 200 - 2*cssLight, but bounded by actual saturation
      const cssLightPercent = l * 100;
      const sliderFromLightness = Math.round((100 - cssLightPercent) * 2);
      return Math.max(0, Math.min(100, sliderFromLightness));
    }
    return 100;
  };

  // Get current hue value - use local state if available, otherwise parse from server data
  const getCurrentHue = (metricKey: string): number => {
    if (localHueValues[metricKey] !== undefined) {
      return localHueValues[metricKey];
    }
    return getHueFromColor(colorPreferences?.[metricKey]?.["4"] || "#39d353");
  };

  // Get current saturation value - use local state if available, otherwise parse from server data
  const getCurrentSaturation = (metricKey: string): number => {
    if (localSaturationValues[metricKey] !== undefined) {
      return localSaturationValues[metricKey];
    }
    return getSaturationFromColor(colorPreferences?.[metricKey]?.["4"] || "#39d353");
  };

  // Generate color palette from local hue+saturation values (for instant preview)
  const getLocalColorPalette = (metricKey: string): Record<number, string> | undefined => {
    const localHue = localHueValues[metricKey];
    if (localHue === undefined) return undefined;

    const localSat = localSaturationValues[metricKey] ?? 100;
    const cssSaturation = localSat;
    const cssLightness = 100 - (localSat / 2);

    const hslToHex = (h: number, s: number, l: number) => {
      l /= 100;
      const a = (s * Math.min(l, 1 - l)) / 100;
      const f = (n: number) => {
        const k = (n + h / 30) % 12;
        const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
        return Math.round(255 * color)
          .toString(16)
          .padStart(2, "0");
      };
      return `#${f(0)}${f(8)}${f(4)}`;
    };

    const baseHex = hslToHex(localHue, cssSaturation, cssLightness);
    return generateColorPalette(baseHex);
  };

  // Intensity scaling helpers
  type IntensityScaling = 'none' | 'proportional' | 'inverse';

  const getIntensityScaling = (metricKey: string): IntensityScaling => {
    const scaling = (colorPreferences as Record<string, any>)?.['_intensity_scaling'];
    return (scaling?.[metricKey] as IntensityScaling) || 'none';
  };

  const setIntensityScaling = (metricKey: string, mode: IntensityScaling) => {
    if (!isPremium) {
      setNonPremiumColorPickerInteraction(true);
      toast.error(t('notifications.toast.custom_chart_colors_are_a_pro_only_feature'));
      navigate("/upgrade");
      return;
    }

    const currentColors = (colorPreferences || {}) as Record<string, any>;
    const currentScaling = (currentColors['_intensity_scaling'] || {}) as Record<string, string>;

    const updatedColors = {
      ...currentColors,
      '_intensity_scaling': {
        ...currentScaling,
        [metricKey]: mode,
      },
    };

    updateColorPreferences.mutate({ colors: updatedColors });
  };

  const getSquareScale = (intensity: number, scaling: IntensityScaling): number => {
    if (scaling === 'none') return 1;
    if (intensity < 0) return 1; // Red (over-goal) squares always full size
    if (intensity === 0) return 0.3; // "no data" squares smaller than the smallest data size

    if (scaling === 'proportional') {
      // Higher intensity = larger square
      switch (intensity) {
        case 1: return 0.5;
        case 2: return 0.65;
        case 3: return 0.8;
        case 4: return 1.0;
        default: return 1;
      }
    } else {
      // inverse: Higher intensity = smaller square
      switch (intensity) {
        case 1: return 1.0;
        case 2: return 0.8;
        case 3: return 0.65;
        case 4: return 0.5;
        default: return 1;
      }
    }
  };

  const resetMetricColors = (metricKey: string) => {
    // Clear local hue/saturation state
    setLocalHueValues((prev) => {
      const { [metricKey]: _removed, ...rest } = prev;
      return rest;
    });
    setLocalSaturationValues((prev) => {
      const { [metricKey]: _removed, ...rest } = prev;
      return rest;
    });

    const currentColors = (colorPreferences || {}) as Record<string, any>;
    const { [metricKey]: _removed, ...remainingColors } = currentColors;

    // Also clear scaling preference for this metric
    if (remainingColors['_intensity_scaling']?.[metricKey]) {
      const { [metricKey]: _removedScaling, ...restScaling } = remainingColors['_intensity_scaling'];
      remainingColors['_intensity_scaling'] = restScaling;
    }

    updateColorPreferences.mutate({ colors: remainingColors });
  };

  // Apply red-to-green preset colors
  const applyRedToGreenPreset = (metricKey: string) => {
    // Track that non-premium user has interacted with color picker
    if (!isPremium) {
      setNonPremiumColorPickerInteraction(true);
      toast.error(t('notifications.toast.custom_chart_colors_are_a_pro_only_feature'));
      navigate("/upgrade");
      return;
    }

    // Clear local hue/saturation state since we're using preset
    setLocalHueValues((prev) => {
      const { [metricKey]: _removed, ...rest } = prev;
      return rest;
    });
    setLocalSaturationValues((prev) => {
      const { [metricKey]: _removed, ...rest } = prev;
      return rest;
    });

    const currentColors = (colorPreferences || {}) as Record<string, Record<string, string>>;
    const updatedColors: Record<string, Record<string, string>> = {
      ...currentColors,
      [metricKey]: redToGreenPreset,
    };

    updateColorPreferences.mutate({ colors: updatedColors });
  };

  // Check if a metric is using the red-to-green preset
  const isRedToGreenPreset = (metricKey: string): boolean => {
    const colors = colorPreferences?.[metricKey];
    if (!colors) return false;
    return colors["1"] === redToGreenPreset["1"] && 
           colors["2"] === redToGreenPreset["2"] && 
           colors["3"] === redToGreenPreset["3"] && 
           colors["4"] === redToGreenPreset["4"];
  };

  // Apply green-to-red preset colors (for inverse metrics)
  const applyGreenToRedPreset = (metricKey: string) => {
    if (!isPremium) {
      setNonPremiumColorPickerInteraction(true);
      toast.error(t('notifications.toast.custom_chart_colors_are_a_pro_only_feature'));
      navigate("/upgrade");
      return;
    }

    setLocalHueValues((prev) => {
      const { [metricKey]: _removed, ...rest } = prev;
      return rest;
    });
    setLocalSaturationValues((prev) => {
      const { [metricKey]: _removed, ...rest } = prev;
      return rest;
    });

    const currentColors = (colorPreferences || {}) as Record<string, Record<string, string>>;
    const updatedColors: Record<string, Record<string, string>> = {
      ...currentColors,
      [metricKey]: greenToRedPreset,
    };

    updateColorPreferences.mutate({ colors: updatedColors });
  };

  // Check if a metric is using the green-to-red preset
  const isGreenToRedPreset = (metricKey: string): boolean => {
    const colors = colorPreferences?.[metricKey];
    if (!colors) return false;
    return colors["1"] === greenToRedPreset["1"] && 
           colors["2"] === greenToRedPreset["2"] && 
           colors["3"] === greenToRedPreset["3"] && 
           colors["4"] === greenToRedPreset["4"];
  };

  const updateGoal = useMutation({
    mutationFn: async ({ metricKey, value, isInverse }: { metricKey: string; value: number; isInverse?: boolean }) => {
      if (!userId) throw new Error("User not found");

      // Map metric keys to goal column names
      const goalMapping: Record<string, string> = {
        calories: "calories_goal",
        fiber: "fiber_goal",
        protein: "protein_goal",
        carbs: "carbs_goal",
        miles_ran: "miles_goal",
        gym_time: "gym_time_goal",
        sleep: "sleep_goal",
        water_gallons: "water_goal",
        body_weight: "body_weight_goal",
        body_fat_percentage: "body_fat_goal",
        github_commits: "github_commits_goal",
        custom_metric: "custom_metric_goal",
        // Support for custom metrics custom_1 through custom_10
        custom_1: "custom_1_goal",
        custom_2: "custom_2_goal",
        custom_3: "custom_3_goal",
        custom_4: "custom_4_goal",
        custom_5: "custom_5_goal",
        custom_6: "custom_6_goal",
        custom_7: "custom_7_goal",
        custom_8: "custom_8_goal",
        custom_9: "custom_9_goal",
        custom_10: "custom_10_goal",
      };

      const goalColumn = goalMapping[metricKey];
      if (!goalColumn) throw new Error("Invalid metric");

      const upsertData: Record<string, unknown> = {
        user_id: userId,
        [goalColumn]: value,
      };
      
      // Include calories_inverse if updating calories goal
      if (metricKey === "calories" && typeof isInverse === "boolean") {
        upsertData.calories_inverse = isInverse;
      }

      const { error } = await supabase.from("user_goals").upsert(
        upsertData,
        {
          onConflict: "user_id",
        },
      );

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["user-goals", userId] });
      toast.success(t('notifications.toast.goal_updated_successfully'));
      setEditingGoal(null);
      setGoalValue("");
    },
    onError: () => {
      toast.error(t('notifications.toast.failed_to_update_goal'));
    },
  });

  const handleEditGoal = (metricKey: string, currentGoal: number, isInverse?: boolean) => {
    setEditingGoal({ metricKey, currentValue: currentGoal, isInverse });
    setGoalValue(currentGoal.toString());
  };

  const handleSaveGoal = () => {
    if (!editingGoal) return;
    const value = parseFloat(goalValue);
    if (isNaN(value) || value <= 0) {
      toast.error(t('notifications.toast.please_enter_a_valid_positive_number'));
      return;
    }
    updateGoal.mutate({ 
      metricKey: editingGoal.metricKey, 
      value,
      isInverse: editingGoal.metricKey === "calories" ? editingGoal.isInverse : undefined
    });
  };

  // Handle day click: Show toast with specific metric value
  const handleDayClick = (date: Date, entry: DailyEntry | undefined, metric: typeof displayMetrics[0]) => {
    const dateStr = format(date, "MMM d, yyyy");
    const dateStrISO = format(date, "yyyy-MM-dd");
    
    // Check if there's a progress snap for this date and metric
    const snapKey = `${dateStrISO}_${metric.key}`;
    const snap = progressSnapsByDateMetric.get(snapKey);
    if (snap) {
      // Collect only snaps for this metric from this date for swipe navigation
      const snapsForMetric = Array.isArray(progressSnaps) 
        ? progressSnaps.filter((s: any) => s.snap_date === dateStrISO && s.metric_name === metric.key)
        : [];
      
      setSelectedSnap(snap);
      setSameDateSnaps(snapsForMetric);
      return;
    }
    
    // Check if this date will be charged
    const isUpcomingCharge = willBeCharged(dateStrISO, metric.key, entry);
    
    if (isUpcomingCharge) {
      // Find the penalty to get charge amount
      const penalty = Array.isArray(activePenalties) ? activePenalties.find((p: any) => p.metric_key === metric.key) : null;
      const daysAgo = Math.floor((new Date().getTime() - date.getTime()) / (1000 * 60 * 60 * 24));
      const daysUntilCharge = 7 - daysAgo;
      
      toast.error(`⚠️ Upcoming Donation`, {
        description: `Your $${penalty?.fee_amount || "?"} commitment donation will be processed in ${daysUntilCharge} day${daysUntilCharge > 1 ? 's' : ''} for ${metric.label} on ${dateStr}`,
        duration: 6000,
      });
      onDateSelect?.(date);
      return;
    }
    
    if (!entry) {
      toast(`${metric.emoji} ${metric.label}`, {
        description: `${dateStr}: ${t('ui.text.no_data_recorded')}`,
      });
      onDateSelect?.(date);
      return;
    }
    
    let value: string | number | null = null;
    const entryData = entry as any;
    
    // Handle boolean metrics
    if (metric.key === "ate_healthy" || metric.key === "low_sugars" || metric.key === "fasted") {
      const boolValue = entryData[metric.key];
      if (boolValue === true) value = "Yes";
      else if (boolValue === false) value = "No";
    }
    // Handle custom metrics
    else if (metric.key.startsWith("custom_") && metric.key !== "custom_metric") {
      const match = metric.key.match(/^custom_(\d+)$/);
      if (match) value = entryData[`custom_${match[1]}_value`];
    }
    // Handle standard metrics
    else {
      const keyMap: Record<string, string> = {
        calories: "calories", fiber: "fiber", added_sugars: "added_sugars", protein: "protein", carbs: "carbs",
        miles_ran: "miles_ran", gym_time: "gym_time", sleep: "sleep",
        water_gallons: "water_gallons", github_commits: "github_commits",
        healthkit_steps: "healthkit_steps", healthkit_exercise_time: "healthkit_exercise_time",
        healthkit_sleep_time: "healthkit_sleep_time", healthkit_stand_hours: "healthkit_stand_hours",
        healthkit_active_energy: "healthkit_active_energy", body_weight: "body_weight",
        body_fat_percentage: "body_fat_percentage", mood: "mood",
        self_appearance_rating: "self_appearance_rating", custom_metric: "custom_metric_value",
      };
      value = entryData[keyMap[metric.key]];
    }
    
    // Apply unit conversion if user has a preference
    const userPreferredUnit = getUnitPreference(metric.key);
    const availableUnits = getAvailableUnits(metric.key);
    let displayUnit = metric.unit;
    let convertedValue = value;
    let convertedGoal = metric.goal;
    
    if (userPreferredUnit && availableUnits.length > 0 && availableUnits.includes(userPreferredUnit)) {
      displayUnit = userPreferredUnit;
      if (typeof value === 'number') {
        convertedValue = convertFromBase(value, metric.key, userPreferredUnit);
      }
      if (metric.goal) {
        convertedGoal = convertFromBase(metric.goal, metric.key, userPreferredUnit);
      }
    }
    
    let displayValue: string;
    if (convertedValue !== null && convertedValue !== undefined) {
      displayValue = typeof convertedValue === "number" 
        ? formatValueWithUnit(convertedValue, displayUnit as any, metric.key)
        : convertedValue;
    } else {
      displayValue = "—";
    }
    
    // Open detailed entries modal instead of showing toast
    console.log("Opening daily entries modal for:", { dateStrISO, metricKey: metric.key, userId });
    const dayData = {
      date: dateStrISO,
      metricKey: metric.key,
      metricName: metric.label,
      metricEmoji: metric.emoji,
      metricUnit: displayUnit,
      isBoolean: metric.key === "ate_healthy" || metric.key === "low_sugars" || metric.key === "fasted",
    };
    console.log("Setting selectedDayForEntries to:", dayData);
    setSelectedDayForEntries(dayData);
    console.log("Setting dailyEntriesModalOpen to: true");
    setDailyEntriesModalOpen(true);
    
    // Also call onDateSelect if provided (for backward compatibility with dashboard)
    onDateSelect?.(date);
  };

  const displayMetrics = (Array.isArray(allMetrics) ? allMetrics : []).filter((m) => activeMetrics.includes(m.key));

  const yearNavigator = isMobile ? (
    // Mobile: Month stepper with left/right arrows
    <div className="flex items-center gap-2">
      <Button 
        variant="ghost" 
        size="sm" 
        onClick={() => setSelectedMonthOffset((prev) => prev - 1)} 
        className="h-7 w-7 p-0"
      >
        <ChevronLeft className="w-4 h-4" />
      </Button>
      <span className="text-sm font-bold min-w-[3rem] text-center">
        {format(addMonths(today, selectedMonthOffset), "MMM yy")}
      </span>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setSelectedMonthOffset((prev) => prev + 1)}
        disabled={selectedMonthOffset >= 0}
        className="h-7 w-7 p-0"
      >
        <ChevronRight className="w-4 h-4" />
      </Button>
    </div>
  ) : (
    // Desktop: Year selector
    <div className="flex items-center gap-2">
      <Button variant="ghost" size="sm" onClick={() => setSelectedYear((prev) => prev - 1)} className="h-7 w-7 p-0">
        <ChevronLeft className="w-4 h-4" />
      </Button>
      <span className="text-sm font-bold min-w-[3rem] text-center">{selectedYear}</span>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setSelectedYear((prev) => prev + 1)}
        disabled={selectedYear >= new Date().getFullYear()}
        className="h-7 w-7 p-0"
      >
        <ChevronRight className="w-4 h-4" />
      </Button>
    </div>
  );

  const filtersSection = <div className="flex flex-col gap-2 items-end">{yearNavigator}</div>;

  // Inject streak aura CSS styles
  useEffect(() => {
    const styleId = 'streak-aura-styles';
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = streakAuraStyles;
      document.head.appendChild(style);
    }
  }, []);

  if (filtersOnly) {
    return filtersSection;
  }

  if (chartOnly) {
    const visibleActiveCount = activeMetrics.filter((k) => availableMetrics.includes(k)).length;
    const hasNoActiveMetrics = !isLoadingCustomMetrics && visibleActiveCount === 0;
    const isLoading = isLoadingCustomMetrics || isLoadingEntries;

    // Generate weeks for condensed view (last 28 days = 4 weeks)
    const generateCondensedWeeks = () => {
      const today = startOfDay(new Date());
      const condensedWeeks: Array<Array<{ date: Date; entry: any } | null>> = [];
      
      // Go back 27 days (28 days total including today)
      for (let weekOffset = 3; weekOffset >= 0; weekOffset--) {
        const week: Array<{ date: Date; entry: any } | null> = [];
        for (let dayOfWeek = 0; dayOfWeek < 7; dayOfWeek++) {
          const daysBack = weekOffset * 7 + (6 - dayOfWeek);
          const date = new Date(today);
          date.setDate(today.getDate() - daysBack);
          
          const dateStr = format(date, "yyyy-MM-dd");
          const entry = entries.find((e: any) => e.entry_date === dateStr);
          week.push({ date, entry: entry || null });
        }
        condensedWeeks.push(week);
      }
      
      return condensedWeeks;
    };

    // Generate weeks for "This Month" view (1st through last day of current month)
    const generateMonthWeeks = () => {
      const today = new Date();
      const monthStart = startOfMonth(today);
      const monthEnd = endOfMonth(today);
      const monthWeeks: Array<Array<{ date: Date; entry: any; isFuture: boolean } | null>> = [];
      
      // Get the day of week for the 1st of the month (0 = Sunday, 1 = Monday, etc.)
      const firstDayOfWeek = getDay(monthStart);
      
      // Start from the beginning of the week containing the 1st
      let currentWeek: Array<{ date: Date; entry: any; isFuture: boolean } | null> = [];
      
      // Add empty slots for days before the 1st
      for (let i = 0; i < firstDayOfWeek; i++) {
        currentWeek.push(null);
      }
      
      // Add all days of the month
      const daysInMonth = eachDayOfInterval({ start: monthStart, end: monthEnd });
      for (const date of daysInMonth) {
        const dateStr = format(date, "yyyy-MM-dd");
        const entry = entries.find((e: any) => e.entry_date === dateStr);
        const isFuture = date > today;
        
        currentWeek.push({ date, entry: entry || null, isFuture });
        
        // If we completed a week (7 days), start a new one
        if (currentWeek.length === 7) {
          monthWeeks.push(currentWeek);
          currentWeek = [];
        }
      }
      
      // Add remaining days of the last week (pad with nulls)
      if (currentWeek.length > 0) {
        while (currentWeek.length < 7) {
          currentWeek.push(null);
        }
        monthWeeks.push(currentWeek);
      }
      
      return monthWeeks;
    };

    const condensedWeeks = generateCondensedWeeks();
    const monthWeeks = generateMonthWeeks();

    // Cycle view mode: full -> 4weeks -> thisMonth -> compactGrid -> full
    const cycleViewMode = () => {
      setCondensedViewMode((current) => {
        const next: CondensedViewMode = current === 'full' ? '4weeks' : current === '4weeks' ? 'thisMonth' : current === 'thisMonth' ? 'compactGrid' : 'full';
        localStorage.setItem('chartCondensedViewMode', next);
        return next;
      });
    };

    // Get view mode icon and title
    const getViewModeInfo = () => {
      switch (condensedViewMode) {
        case '4weeks':
          return { icon: CalendarDays, title: 'This Month' };
        case 'thisMonth':
          return { icon: Grip, title: 'Compact Grid' };
        case 'compactGrid':
          return { icon: LayoutList, title: 'Full Year' };
        default:
          return { icon: LayoutGrid, title: 'Last 4 Weeks' };
      }
    };

    const viewModeInfo = getViewModeInfo();

    return (
      <div className="relative contribution-chart-container">
        {/* View mode label - centered */}
        {condensedViewMode === '4weeks' && (
          <span className="absolute -top-1 left-0 right-0 text-center text-[10px] text-muted-foreground/60">
            Last 4 Weeks
          </span>
        )}
        {condensedViewMode === 'thisMonth' && (
          <span className="absolute -top-1 left-0 right-0 text-center text-[10px] text-muted-foreground/60">
            {format(new Date(), 'MMMM yyyy')}
          </span>
        )}
        {condensedViewMode === 'compactGrid' && (
          <span className="absolute -top-1 left-0 right-0 text-center text-[10px] text-muted-foreground/60">
            Compact Grid
          </span>
        )}

        {/* View switcher button */}
        <Button
          variant="ghost"
          size="sm"
          className="absolute -top-2 right-0 z-30 h-8 w-8 p-0 hover:bg-muted"
          onClick={cycleViewMode}
          title={viewModeInfo.title}
        >
          <viewModeInfo.icon className="w-4 h-4 text-muted-foreground" />
        </Button>

        <div className={isMobile ? "" : "flex gap-6"}>
          <div className={isMobile ? "space-y-6" : "flex-1 space-y-6"}>
          {isLoading ? (
            // Loading skeleton for charts - matches the current view mode
            condensedViewMode === '4weeks' || condensedViewMode === 'thisMonth' || condensedViewMode === 'compactGrid' ? (
              // Grid skeleton for condensed views (4weeks / thisMonth / compactGrid)
              <div className={condensedViewMode === 'compactGrid' ? "space-y-3 pt-6" : "grid grid-cols-3 gap-x-3 gap-y-5 pt-6"}>
                {[1, 2, 3, 4, 5, 6].map((i) => (
                  <div key={i} className="flex flex-col items-center space-y-2">
                    {/* Title placeholder */}
                    <div className="h-4 w-16 bg-muted animate-pulse rounded"></div>
                    {/* Grid placeholder */}
                    <div className="flex gap-1">
                      {Array.from({ length: condensedViewMode === '4weeks' ? 4 : 5 }).map((_, weekIndex) => (
                        <div key={weekIndex} className="flex flex-col gap-1">
                          {Array.from({ length: 7 }).map((_, dayIndex) => (
                            <div
                              key={dayIndex}
                              className={`${condensedViewMode === '4weeks' ? 'w-5 h-5' : 'w-4 h-4'} rounded-sm bg-muted animate-pulse`}
                              style={{ animationDelay: `${(weekIndex * 7 + dayIndex) * 30}ms` }}
                            />
                          ))}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              // Full year skeleton - vertical stacked charts
              <div className="space-y-6">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="space-y-2">
                    <div className="h-6 w-32 bg-muted animate-pulse rounded"></div>
                    <div className="h-24 w-full bg-muted animate-pulse rounded"></div>
                  </div>
                ))}
              </div>
            )
          ) : hasNoActiveMetrics ? (
            // Show blurred placeholder when no metrics selected
            <div className="relative">
              <div className="blur-sm pointer-events-none opacity-40">
                <div className="space-y-2">
                  <h3 className="text-lg font-semibold">🔥 Placeholder Metric</h3>
                  <div className="flex gap-1">
                    <div className="flex flex-col gap-1 pr-1">
                      <div className="h-3 text-[9px] text-muted-foreground/50 flex items-center"></div>
                      <div className="h-3 text-[9px] text-muted-foreground/50 flex items-center"></div>
                      <div className="h-3 text-[9px] text-muted-foreground/50 flex items-center -mt-1">Mon</div>
                      <div className="h-3 text-[9px] text-muted-foreground/50 flex items-center"></div>
                      <div className="h-3 text-[9px] text-muted-foreground/50 flex items-center -mt-1">Wed</div>
                      <div className="h-3 text-[9px] text-muted-foreground/50 flex items-center"></div>
                      <div className="h-3 text-[9px] text-muted-foreground/50 flex items-center -mt-1">Fri</div>
                      <div className="h-3 text-[9px] text-muted-foreground/50 flex items-center"></div>
                    </div>
                    <div className="flex-1 overflow-x-hidden">
                      <div className="flex gap-1">
                        {weeks.map((week, weekIndex) => (
                          <div key={weekIndex} className="flex w-3 flex-col gap-1 items-center flex-none">
                            <div className="w-3 h-3 text-[9px] text-muted-foreground/50 text-center leading-3">
                              {weekIndex % 4 === 0 ? "Jan" : ""}
                            </div>
                            {[0, 1, 2, 3, 4, 5, 6].map((dayIndex) => {
                              const randomIntensity = Math.floor(Math.random() * 5);
                              return (
                                <div
                                  key={dayIndex}
                                  className="w-3 h-3 rounded-sm select-none"
                                  style={{
                                    backgroundColor: defaultColors[randomIntensity as keyof typeof defaultColors],
                                  }}
                                />
                              );
                            })}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Centered Empty State */}
              <div className="absolute inset-0 flex items-center justify-center pt-12">
                {hasNoActiveMetrics && (
                  isOwnProfile ? (
                    isMobile ? (
                      <Button 
                        variant="default"
                        onClick={() => navigate("/track")}
                        className="text-sm animate-[pulse_2s_ease-in-out_infinite] hover:animate-none hover:scale-105 transition-transform"
                      >{t('ui.text.start_tracking_now')}</Button>
                    ) : (
                      <p className="text-muted-foreground/60 text-sm">{t('ui.text.add_a_metric_to_get_started')}</p>
                    )
                  ) : (
                    <p className="text-muted-foreground text-sm">{t('ui.text.user_displays_no_data')}</p>
                  )
                )}
              </div>
            </div>
          ) : condensedViewMode === '4weeks' ? (
            /* Last 4 Weeks view - 3 column grid with 28 days per chart */
            <div className="grid grid-cols-3 gap-x-3 gap-y-5 pt-6">
              {activeMetrics
                .map((metricKey) => displayMetrics.find((m) => m.key === metricKey))
                .filter((metric): metric is typeof displayMetrics[0] => metric !== undefined)
                .map((metric) => {
                  const customColors = colorPreferences?.[metric.key];
                  
                  return (
                    <div 
                      key={metric.key} 
                      className="flex flex-col items-start space-y-2"
                    >
                      {/* Title - smaller, not clickable */}
                      <div className="text-xs font-medium text-foreground truncate max-w-full text-left">
                        <span style={{ fontSize: '1.7em', lineHeight: 1 }}>{metric.emoji}</span> {metric.label}
                      </div>
                      
                      {/* 4x7 grid of squares (28 days) */}
                      <div className="flex gap-1">
                        {condensedWeeks.map((week, weekIndex) => (
                          <div key={weekIndex} className="flex flex-col gap-1">
                            {week.map((day, dayIndex) => {
                              if (!day) {
                                return <div key={dayIndex} className="w-5 h-5" />;
                              }
                              const rawIntensity = metric.getValue(day.entry);
                              const dateStr = format(day.date, "yyyy-MM-dd");
                              const intensity = getDisplayIntensity(metric.key, dateStr, rawIntensity);
                              const customColor = customColors?.[intensity.toString()];
                              
                              const scaling4w = getIntensityScaling(metric.key);
                              const scale4w = getSquareScale(intensity, scaling4w);
                              return (
                                <div key={dayIndex} className="relative">
                                  <div
                                    className={`w-5 h-5 rounded-sm cursor-pointer hover:ring-1 hover:ring-primary transition-all ${format(day.date, 'yyyy-MM-dd') === format(today, 'yyyy-MM-dd') ? 'today-glow' : ''}`}
                                    style={{
                                      backgroundColor: customColor || (intensity < 0 
                                        ? redColors[intensity.toString() as keyof typeof redColors]
                                        : defaultColors[intensity as keyof typeof defaultColors]),
                                      ...(scale4w < 1 ? { scale: `${scale4w}`, transformOrigin: 'center' } : {})
                                    }}
                                    title={`${format(day.date, "MMM d, yyyy")} - Intensity: ${intensity}`}
                                    onClick={() => handleDayClick(day.date, day.entry, metric)}
                                  />
                                  {intensity === 0 && noDataSet.has(`${dateStr}_${metric.key}`) && (
                                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                      <X className="w-2.5 h-2.5 text-muted-foreground/30" strokeWidth={2.5} />
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
            </div>
          ) : condensedViewMode === 'thisMonth' ? (
            /* This Month view - 3 column grid with current month days (smaller squares for 5+ weeks) */
            <div className="grid grid-cols-3 gap-x-3 gap-y-5 pt-6">
              {activeMetrics
                .map((metricKey) => displayMetrics.find((m) => m.key === metricKey))
                .filter((metric): metric is typeof displayMetrics[0] => metric !== undefined)
                .map((metric) => {
                  const customColors = colorPreferences?.[metric.key];
                  // Use smaller squares if we have 5 or more weeks
                  const squareSize = monthWeeks.length >= 5 ? 'w-4 h-4' : 'w-5 h-5';
                  
                  return (
                    <div 
                      key={metric.key} 
                      className="flex flex-col items-start space-y-2"
                    >
                      {/* Title - smaller, not clickable */}
                      <div className="text-xs font-medium text-foreground truncate max-w-full text-left">
                        <span style={{ fontSize: '1.7em', lineHeight: 1 }}>{metric.emoji}</span> {metric.label}
                      </div>
                      
                      {/* Month grid of squares */}
                      <div className="flex gap-1">
                        {monthWeeks.map((week, weekIndex) => (
                          <div key={weekIndex} className="flex flex-col gap-1">
                            {week.map((day, dayIndex) => {
                              if (!day) {
                                return <div key={dayIndex} className={squareSize} />;
                              }
                              const rawIntensity = day.isFuture ? 0 : metric.getValue(day.entry);
                              const dateStr = format(day.date, "yyyy-MM-dd");
                              const intensity = day.isFuture ? 0 : getDisplayIntensity(metric.key, dateStr, rawIntensity);
                              const customColor = customColors?.[intensity.toString()];
                              
                              const scalingMonth = getIntensityScaling(metric.key);
                              const scaleMonth = getSquareScale(intensity, scalingMonth);
                              return (
                                <div key={dayIndex} className="relative">
                                  <div
                                    className={`${squareSize} rounded-sm cursor-pointer hover:ring-1 hover:ring-primary transition-all ${day.isFuture ? 'opacity-40' : ''} ${format(day.date, 'yyyy-MM-dd') === format(today, 'yyyy-MM-dd') ? 'today-glow' : ''}`}
                                    style={{
                                      backgroundColor: customColor || (intensity < 0 
                                        ? redColors[intensity.toString() as keyof typeof redColors]
                                        : defaultColors[intensity as keyof typeof defaultColors]),
                                      ...(scaleMonth < 1 ? { scale: `${scaleMonth}`, transformOrigin: 'center' } : {})
                                    }}
                                    title={`${format(day.date, "MMM d, yyyy")}${day.isFuture ? ' (future)' : ''}`}
                                    onClick={() => !day.isFuture && handleDayClick(day.date, day.entry, metric)}
                                  />
                                  {!day.isFuture && intensity === 0 && noDataSet.has(`${dateStr}_${metric.key}`) && (
                                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                      <X className="w-2.5 h-2.5 text-muted-foreground/30" strokeWidth={2.5} />
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
            </div>
          ) : condensedViewMode === 'compactGrid' ? (
            /* Compact Grid view - full year grids, no month/day labels, smaller titles, minimal toolbar */
            <div className="space-y-5 pt-6">
              {activeMetrics
                .map((metricKey) => displayMetrics.find((m) => m.key === metricKey))
                .filter((metric): metric is typeof displayMetrics[0] => metric !== undefined)
                .map((metric) => {
                  const customColors = colorPreferences?.[metric.key];
                  
                  return (
                    <div key={metric.key} className="space-y-1.5">
                      {/* Title row with range legend on the right */}
                      <div className="flex items-center justify-between">
                        {/* Compact title */}
                        <div className="text-[11px] font-medium text-foreground truncate">
                          <span style={{ fontSize: '1.2em', lineHeight: 1 }}>{metric.emoji}</span> {metric.label}
                        </div>
                        
                        {/* Range legend - right aligned */}
                        <div className="flex items-center gap-1 text-[9px] flex-shrink-0 ml-2">
                          {metric.key === "ate_healthy" || metric.key === "low_sugars" || metric.key === "fasted" || (metric as any).isBoolean ? (
                            <>
                              <span className="text-muted-foreground">{t('common.no')}</span>
                              <div className="flex gap-0.5">
                                {[0, 4].map((level) => {
                                  const useDefaultColors = (metric as any).isBoolean && (metric as any).isInverse;
                                  const customColor = useDefaultColors ? null : customColors?.[level.toString()];
                                  const backgroundColor = customColor 
                                    ? customColor 
                                    : (useDefaultColors && level === 4) 
                                      ? redColors["-4"]
                                      : defaultColors[level as keyof typeof defaultColors];
                                  return (
                                    <div
                                      key={level}
                                      style={{ backgroundColor, width: "10px", height: "10px", borderRadius: "1px" }}
                                    />
                                  );
                                })}
                              </div>
                              <span className="text-muted-foreground">{t('common.yes')}</span>
                            </>
                          ) : (
                            <>
                              <span className="text-muted-foreground">0{shouldShowUnit(metric.label, metric.unit) ? metric.unit : ''}</span>
                              <div className="flex gap-0.5">
                                {[0, 1, 2, 3, 4].map((level) => {
                                  const customColor = customColors?.[level.toString()];
                                  return (
                                    <div
                                      key={level}
                                      style={{
                                        backgroundColor: customColor || defaultColors[level as keyof typeof defaultColors],
                                        width: "10px",
                                        height: "10px",
                                        borderRadius: "1px",
                                      }}
                                    />
                                  );
                                })}
                              </div>
                              {!metric.isRelative && (
                                <span className="text-muted-foreground">
                                  {metric.goal}{shouldShowUnit(metric.label, metric.unit) ? metric.unit : ''}
                                </span>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                      
                      {/* Grid - no month labels, no day labels, fills full width */}
                      <div className="w-full">
                        <div className="flex gap-[2px] w-full">
                          {weeks.map((week, weekIndex) => (
                            <div key={weekIndex} className="flex flex-1 flex-col gap-[2px] items-center">
                              {week.map((day, dayIndex) => {
                                if (!day) {
                                  return <div key={dayIndex} className="w-full aspect-square" />;
                                }
                                const rawIntensity = metric.getValue(day.entry);
                                const dateStr = format(day.date, "yyyy-MM-dd");
                                const intensity = getDisplayIntensity(metric.key, dateStr, rawIntensity);
                                const customColor = customColors?.[intensity.toString()];
                                const scalingCG = getIntensityScaling(metric.key);
                                const scaleCG = getSquareScale(intensity, scalingCG);
                                
                                return (
                                  <div key={dayIndex} className="relative w-full aspect-square">
                                    <div
                                      className={`w-full h-full rounded-[1px] cursor-pointer hover:ring-1 hover:ring-primary transition-all ${format(day.date, 'yyyy-MM-dd') === format(today, 'yyyy-MM-dd') ? 'today-glow' : ''}`}
                                      style={{
                                        backgroundColor: customColor || (intensity < 0 
                                          ? redColors[intensity.toString() as keyof typeof redColors]
                                          : defaultColors[intensity as keyof typeof defaultColors]),
                                        ...(scaleCG < 1 ? { scale: `${scaleCG}`, transformOrigin: 'center' } : {})
                                      }}
                                      title={`${format(day.date, "MMM d, yyyy")} - Intensity: ${intensity}`}
                                      onClick={() => handleDayClick(day.date, day.entry, metric)}
                                    />
                                    {intensity === 0 && noDataSet.has(`${dateStr}_${metric.key}`) && (
                                      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                        <X className="w-[60%] h-[60%] text-muted-foreground/25" strokeWidth={2.5} />
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  );
                })}
            </div>
          ) : (
            activeMetrics
              .map((metricKey) => displayMetrics.find((m) => m.key === metricKey))
              .filter((metric): metric is typeof displayMetrics[0] => metric !== undefined)
              .map((metric, activeIndex) => {
                // Calculate if this metric has an active streak
                const onStreak = hasActiveStreak(metric.key, entries, metric, userGoals, customMetrics);
                
                // Get the chart color for the aura (from custom colors or default)
                const customColors = colorPreferences?.[metric.key];
                const chartColor = customColors?.['4'] || defaultColors[4]; // Use highest intensity color
                
                return (
                <div 
                  key={metric.key} 
                  className="space-y-2 relative"
                  data-chart-index={activeIndex}
                  data-chart-metric={metric.key}
                >
                  {/* Streak aura effect */}
                  {onStreak && (
                    <>
                      <div 
                        className="streak-aura"
                        style={{ 
                          background: `radial-gradient(ellipse at center, ${chartColor}40 0%, ${chartColor}20 40%, transparent 70%)`,
                        }}
                      />
                      <div 
                        className="streak-aura"
                        style={{ 
                          background: `radial-gradient(ellipse at center, ${chartColor}30 0%, transparent 60%)`,
                        }}
                      />
                    </>
                  )}
                  
                  <div className="flex items-center gap-2 relative z-10">
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (isOwnProfile) {
                            openQuickEditForMetric(metric.key);
                          }
                        }}
                        disabled={!isOwnProfile}
                        className="text-left text-lg font-semibold inline-flex items-center gap-1 group focus:outline-none touch-manipulation cursor-pointer active:opacity-70 disabled:cursor-default disabled:opacity-100 min-h-[44px] -my-2 py-2"
                        aria-label={`Open quick edit for ${metric.label}`}
                      >
                        <span>
                          {metric.emoji} {metric.label}
                        </span>
                        {isOwnProfile && (
                          <span
                            aria-hidden="true"
                            className="ml-0.5 text-muted-foreground/40 group-hover:text-muted-foreground/60 group-active:text-muted-foreground/60 font-light"
                            style={{ transform: "translateY(1px)" }}
                          >
                            &gt;
                          </span>
                        )}
                      </button>
                      {/* Screenshot mode: Dice button to randomize this graph's data */}
                      {showDiceButtons && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            console.log('[Screenshot] Dice clicked, metric object:', {
                              key: metric.key,
                              isBoolean: (metric as any).isBoolean,
                              metric_type: metric.metric_type,
                              isInverse: (metric as any).isInverse,
                              is_inverse: (metric as any).is_inverse,
                              fullMetric: metric
                            });
                            randomizeMetricData(
                              metric.key, 
                              metric.goal || 100, 
                              (metric as any).isInverse || (metric as any).is_inverse || false,
                              (metric as any).isBoolean || metric.metric_type === "boolean"
                            );
                          }}
                          className="p-1.5 rounded-md bg-primary/10 hover:bg-primary/20 text-primary transition-colors"
                        >
                          <Dices className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="relative flex gap-1 items-center z-10">
                    {/* Y-axis labels - conditional based on view mode */}
                    {isBarMode(metric.key) ? (
                      <div className="flex flex-col gap-1 pr-1">
                        <div className="h-3 text-[9px] text-muted-foreground/50 flex items-center"></div>
                        <div className="h-3 text-[9px] text-muted-foreground/50 flex items-center"></div>
                        <div className="h-3 text-[9px] text-muted-foreground/50 flex items-center text-right relative" style={{ top: '-5px' }}>Mon</div>
                        <div className="h-3 text-[9px] text-muted-foreground/50 flex items-center"></div>
                        <div className="h-3 text-[9px] text-muted-foreground/50 flex items-center text-right relative" style={{ top: '-4px' }}>Wed</div>
                        <div className="h-3 text-[9px] text-muted-foreground/50 flex items-center"></div>
                        <div className="h-3 text-[9px] text-muted-foreground/50 flex items-center text-right relative" style={{ top: '-2px' }}>Fri</div>
                        <div className="h-3 text-[9px] text-muted-foreground/50 flex items-center"></div>
                      </div>
                    ) : (
                      <div className="flex flex-col justify-between pr-1" style={{ height: '75px' }}>
                        {(() => {
                          const currentMode = getViewMode(metric.key);
                          if (!currentMode || currentMode === 'bar') return null;
                          
                          const dataPoints = processLineChartData(weeks, metric, currentMode);
                          const maxValue = Math.max(...dataPoints.map(d => d.y), 1);
                          const steps = 4;
                          
                          // Generate unique labels with better spacing
                          const labels: number[] = [];
                          const stepValue = maxValue / steps;
                          
                          for (let i = 0; i <= steps; i++) {
                            const value = maxValue - (i * stepValue);
                            // Only add if it's significantly different from the last label
                            if (labels.length === 0 || Math.abs(value - labels[labels.length - 1]) > maxValue * 0.05) {
                              labels.push(value);
                            }
                          }
                          
                          // If we have too few labels, ensure we at least show max and min
                          if (labels.length < 2) {
                            labels.length = 0;
                            labels.push(maxValue, 0);
                          }
                          
                          // Pad to 5 labels for consistent spacing
                          while (labels.length < 5) {
                            labels.push(labels[labels.length - 1]);
                          }
                          
                          return labels.slice(0, 5).map((value, i) => {
                            const displayValue = maxValue < 10 ? value.toFixed(1) : Math.round(value);
                            return (
                              <div key={i} className="h-3 text-[9px] text-muted-foreground/50 flex items-center text-right pr-1">
                                {displayValue}
                              </div>
                            );
                          });
                        })()}
                      </div>
                    )}
                    
                    {/* Chart area - conditional rendering */}
                    <div className="flex-1 overflow-x-hidden overflow-y-hidden pb-2.5">
                      {isBarMode(metric.key) ? (
                        // Bar chart (original grid view)
                        <div className="flex gap-1">
                          {weeks.map((week, weekIndex) => (
                            <div key={weekIndex} className="flex w-3 flex-col gap-1 items-center flex-none">
                              <div className="w-3 h-3 text-[9px] text-muted-foreground/50 text-center leading-3">
                                {getMonthLabel(weekIndex)}
                              </div>
                              {week.map((day, dayIndex) => {
                                if (!day) {
                                  // Empty square for dates outside the year
                                  return <div key={dayIndex} className="w-3 h-3" />;
                                }
                                const rawIntensity = metric.getValue(day.entry);
                                const dateStr = format(day.date, "yyyy-MM-dd");
                                const intensity = getDisplayIntensity(metric.key, dateStr, rawIntensity);
                                const isPenalized = penalizedMetrics.get(dateStr)?.has(metric.key) || false;
                                const upcomingCharge = willBeCharged(dateStr, metric.key, day.entry);
                                const customColors = colorPreferences?.[metric.key];
                                const snapKey = `${dateStr}_${metric.key}`;
                                const hasSnap = progressSnapsByDateMetric.has(snapKey);
                                
                                // Determine background color based on intensity
                                let bgColor = "";
                                let bgStyle: { backgroundColor?: string } | undefined = undefined;
                                
                                if (isPenalized) {
                                  bgColor = "bg-red-600";
                                } else if (customColors?.[intensity.toString()]) {
                                  // Use inline style for custom colors (Tailwind JIT can't see dynamic classes)
                                  bgStyle = { backgroundColor: customColors[intensity.toString()] };
                                } else {
                                  bgColor = getSquareColor(intensity, false, metric.key);
                                  // Use red colors for negative intensities (calorie overshoot), green for positive
                                  if (intensity < 0) {
                                    bgStyle = { backgroundColor: redColors[intensity.toString() as keyof typeof redColors] };
                                  } else {
                                    bgStyle = { backgroundColor: defaultColors[intensity as keyof typeof defaultColors] };
                                  }
                                }

                                return (
                                  <div
                                    key={dayIndex}
                                    className="relative cursor-pointer"
                                    onClick={() => handleDayClick(day.date, day.entry, metric)}
                                    style={{
                                      // Larger touch target for mobile
                                      padding: isMobile ? '3px' : '0',
                                      margin: isMobile ? '-3px' : '0',
                                      touchAction: 'manipulation', // Prevent double-tap zoom
                                    }}
                                  >
                                    {(() => { const scalingFY = getIntensityScaling(metric.key); const scaleFY = getSquareScale(intensity, scalingFY); return (
                                    <div
                                      className={`w-3 h-3 rounded-sm select-none ${hasSnap ? 'border' : bgColor} ${onStreak ? 'streak-wave-square' : ''} ${format(day.date, 'yyyy-MM-dd') === format(today, 'yyyy-MM-dd') ? 'today-glow' : ''} hover:ring-2 hover:ring-primary transition-all`}
                                      style={{
                                        ...(hasSnap ? {
                                          backgroundColor: 'transparent',
                                          borderColor: bgStyle?.backgroundColor || customColors?.[intensity.toString()] || (intensity === 0 ? 'rgb(229 231 235)' : undefined)
                                        } : (
                                          bgStyle ||
                                          (customColors?.[intensity.toString()]
                                            ? { backgroundColor: customColors[intensity.toString()] }
                                            : undefined)
                                        )),
                                        ...(onStreak ? { animationDelay: `${weekIndex * 0.05}s` } : {}),
                                        ...(scaleFY < 1 ? { scale: `${scaleFY}`, transformOrigin: 'center' } as React.CSSProperties : {})
                                      }}
                                      title={`${format(day.date, "MMM d, yyyy")}${isPenalized ? " - Donation Processed" : upcomingCharge ? " - Upcoming Donation" : ` - Intensity: ${intensity}`}`}
                                    />
                                    ); })()}
                                    {upcomingCharge && !isPenalized && (
                                      <div 
                                        className="absolute inset-0 flex items-center justify-center pointer-events-none"
                                        style={{ fontSize: '8px', lineHeight: '12px' }}
                                      >
                                        ⚠️
                                      </div>
                                    )}
                                    {hasSnap && (
                                      <div 
                                        className="absolute inset-0 flex items-center justify-center pointer-events-none"
                                      >
                                        <Camera className="w-2 h-2 text-black dark:text-white system:text-white" strokeWidth={1.5} />
                                      </div>
                                    )}
                                    {intensity === 0 && noDataSet.has(`${dateStr}_${metric.key}`) && (
                                      <div 
                                        className="absolute inset-0 flex items-center justify-center pointer-events-none"
                                      >
                                        <X className="w-2 h-2 text-muted-foreground/30" strokeWidth={2.5} />
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          ))}
                        </div>
                      ) : (
                        // Line chart view
                        <div className="relative" style={{ height: '75px' }}>
                          {/* Month labels */}
                          <div className="absolute top-0 left-0 right-0 flex">
                            {weeks.map((_, weekIndex) => (
                              <div key={weekIndex} className="flex-none text-[9px] text-muted-foreground/50 text-center" style={{ width: '12px' }}>
                                {getMonthLabel(weekIndex)}
                              </div>
                            ))}
                          </div>
                          
                          {/* Camera icons overlay - positioned absolutely above chart */}
                          {(() => {
                            const currentMode = getViewMode(metric.key);
                            if (!currentMode || currentMode === 'bar') return null;
                            
                            const dataPoints = processLineChartData(weeks, metric, currentMode);
                            if (dataPoints.length === 0) return null;
                            
                            const maxValue = Math.max(...dataPoints.map(d => d.y), 1);
                            const padding = 3;
                            const chartHeight = 59 - (padding * 2);
                            const totalWidth = weeks.length * 7 * 3;
                            
                            return dataPoints.filter(p => p.hasSnap).map((point, i) => {
                              const xPercent = (point.x * 3 / totalWidth) * 100;
                              const cy = padding + chartHeight - ((point.y / maxValue) * chartHeight);
                              const yPixels = 14 + cy; // 14px offset from top + cy position
                              // Get the first snap date for initial display
                              const firstSnapDate = point.snapDates && point.snapDates.length > 0 ? point.snapDates[0] : format(point.date, "yyyy-MM-dd");
                              const snapKey = `${firstSnapDate}_${metric.key}`;
                              const snap = progressSnapsByDateMetric.get(snapKey);
                              
                              // Check if camera would overflow top - if so, position below
                              const wouldOverflowTop = (yPixels - 32) < 0;
                              const iconTop = wouldOverflowTop ? yPixels + 8 : yPixels - 32;
                              const lineStyle = wouldOverflowTop 
                                ? { width: '1px', height: '8px', bottom: '20px', transform: 'translateX(-50%)' }
                                : { width: '1px', height: '26px', top: '10px', transform: 'translateX(-50%)' };
                              
                              return (
                                <div
                                  key={`camera-${i}`}
                                  className="absolute cursor-pointer z-20"
                                  style={{
                                    left: `${xPercent}%`,
                                    top: `${iconTop}px`,
                                    transform: 'translateX(-50%)',
                                  }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (snap && point.snapDates) {
                                      // Collect snaps for this metric from ALL dates in the week
                                      const snapsForMetric = Array.isArray(progressSnaps) 
                                        ? progressSnaps.filter((s: any) => 
                                            point.snapDates!.includes(s.snap_date) && s.metric_name === metric.key
                                          ).sort((a, b) => a.snap_date.localeCompare(b.snap_date) || a.created_at.localeCompare(b.created_at))
                                        : [];
                                      // Set the first snap in the sorted array as the selected snap
                                      setSelectedSnap(snapsForMetric.length > 0 ? snapsForMetric[0] : snap);
                                      setSameDateSnaps(snapsForMetric);
                                    }
                                  }}
                                >
                                  {/* Connecting line */}
                                  <div 
                                    className="absolute left-1/2 bg-white/30 pointer-events-none"
                                    style={lineStyle}
                                  />
                                  <Camera className="w-5 h-5 text-black dark:text-white system:text-white relative z-10" strokeWidth={1.5} />
                                </div>
                              );
                            });
                          })()}
                          
                          {/* SVG line chart */}
                          <svg 
                            className="absolute" 
                            style={{ top: '14px', left: 0, width: '100%', height: '59px' }}
                            preserveAspectRatio="none"
                            viewBox={`0 0 ${weeks.length * 7 * 3} 59`}
                          >
                            {(() => {
                              const currentMode = getViewMode(metric.key);
                              if (!currentMode || currentMode === 'bar') return null;
                              
                              const dataPoints = processLineChartData(weeks, metric, currentMode);
                              if (dataPoints.length === 0) return null;
                              
                              const maxValue = Math.max(...dataPoints.map(d => d.y), 1);
                              const chartColor = getChartColor(metric.key);
                              const padding = 3; // Padding at top and bottom to prevent clipping
                              const chartHeight = 59 - (padding * 2); // Usable chart height
                              
                              // Create path with padding
                              const pathData = dataPoints.map((point, i) => {
                                const x = point.x * 3;
                                const y = padding + chartHeight - ((point.y / maxValue) * chartHeight);
                                return `${i === 0 ? 'M' : 'L'} ${x} ${y}`;
                              }).join(' ');
                              
                              // Create area fill path
                              const areaPath = pathData + ` L ${dataPoints[dataPoints.length - 1].x * 3} ${59 - padding} L 0 ${59 - padding} Z`;
                              const gradientId = `gradient-${metric.key}`;
                              
                              return (
                                <>
                                  {/* Gradient definition */}
                                  <defs>
                                    <linearGradient id={gradientId} x1="0%" y1="0%" x2="0%" y2="100%">
                                      <stop offset="0%" stopColor={chartColor} stopOpacity="0.1" />
                                      <stop offset="100%" stopColor={chartColor} stopOpacity="0" />
                                    </linearGradient>
                                  </defs>
                                  {/* Area fill with gradient */}
                                  <path
                                    d={areaPath}
                                    fill={`url(#${gradientId})`}
                                  />
                                  {/* Bottom white line */}
                                  <line
                                    x1="0"
                                    y1={59 - padding}
                                    x2={dataPoints[dataPoints.length - 1].x * 3}
                                    y2={59 - padding}
                                    stroke="white"
                                    strokeWidth="1"
                                    strokeOpacity="0.05"
                                  />
                                  {/* Line */}
                                  <path
                                    d={pathData}
                                    stroke={chartColor}
                                    strokeWidth="1.5"
                                    fill="none"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                  {/* Data points */}
                                  {dataPoints.map((point, i) => {
                                    const cx = point.x * 3;
                                    const cy = padding + chartHeight - ((point.y / maxValue) * chartHeight);
                                    return (
                                      <circle
                                        key={i}
                                        cx={cx}
                                        cy={cy}
                                        r="1.5"
                                        fill={chartColor}
                                        className="cursor-pointer hover:r-2"
                                        onClick={() => {
                                          const day = weeks[Math.floor(point.x / 7)]?.[point.x % 7];
                                          if (day) handleDayClick(day.date, day.entry, metric);
                                        }}
                                      >
                                        <title>{`${format(point.date, "MMM d, yyyy")}: ${Math.round(point.y)}`}</title>
                                      </circle>
                                    );
                                  })}
                                </>
                              );
                            })()}
                          </svg>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className={metric.key === "github_commits" ? "text-[10px] mt-2 ml-9" : "flex items-center flex-wrap gap-x-2 gap-y-1 text-[10px] mt-2 ml-9 relative"}>
                    {/* Boolean/flag metrics or custom boolean metrics */}
                    {metric.key === "ate_healthy" || metric.key === "low_sugars" || metric.key === "fasted" || (metric as any).isBoolean ? (
                      <>
                        <span>{t('common.no')}</span>
                        <div className="flex gap-1">
                          {[0, 4].map((level) => {
                            // For inverse boolean metrics, always use default colors (red/green)
                            const useDefaultColors = (metric as any).isBoolean && (metric as any).isInverse;
                            const customColor = useDefaultColors ? null : colorPreferences?.[metric.key]?.[level.toString()];
                            
                            // For inverse boolean metrics, use red for "Yes" (level 4)
                            const backgroundColor = customColor 
                              ? customColor 
                              : (useDefaultColors && level === 4) 
                                ? redColors["-4"]
                                : defaultColors[level as keyof typeof defaultColors];
                            
                            return (
                              <div
                                key={level}
                                className={customColor ? "" : getSquareColor(level, false, metric.key)}
                                style={{
                                  backgroundColor,
                                  width: "12px",
                                  height: "12px",
                                  borderRadius: "2px",
                                }}
                              />
                            );
                          })}
                        </div>
                        <span>{t('common.yes')}</span>
                        {/* Hide color picker for inverse boolean metrics */}
                        {isOwnProfile && !((metric as any).isBoolean && (metric as any).isInverse) && (
                          <Popover onOpenChange={(open) => handleColorPickerOpenChange(open, metric.key)}>
                            <PopoverTrigger asChild>
                              <Button variant="ghost" size="sm" className="h-4 w-4 p-0 hover:bg-transparent ml-1">
                                <Palette className="w-3 h-3 text-muted-foreground hover:text-foreground" />
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-64 bg-popover p-3" align="start">
                              <div className="space-y-3">
                                <div className="text-xs font-semibold">{t('ui.text.customize_color')}</div>
                                <div className="space-y-2">
                                  <div className="text-xs text-muted-foreground">{t('ui.text.choose_a_hue_for_your_custom_gradient')}</div>
                                  <div className="relative">
                                    <div 
                                      className="w-full h-8 rounded mb-2"
                                      style={{
                                        background: 'linear-gradient(to right, hsl(0, 100%, 50%), hsl(60, 100%, 50%), hsl(120, 100%, 50%), hsl(180, 100%, 50%), hsl(240, 100%, 50%), hsl(300, 100%, 50%), hsl(360, 100%, 50%))'
                                      }}
                                    />
                                    <input
                                      type="range"
                                      min="0"
                                      max="360"
                                      value={getCurrentHue(metric.key)}
                                      onChange={(e) => handleColorChange(metric.key, parseInt(e.target.value), getCurrentSaturation(metric.key))}
                                      className="w-full cursor-pointer"
                                    />
                                  </div>
                                  <div className="relative mt-2">
                                    <div 
                                      className="w-full h-6 rounded mb-2"
                                      style={{
                                        background: `linear-gradient(to right, white, hsl(${getCurrentHue(metric.key)}, 100%, 50%))`
                                      }}
                                    />
                                    <input
                                      type="range"
                                      min="0"
                                      max="100"
                                      value={getCurrentSaturation(metric.key)}
                                      onChange={(e) => handleColorChange(metric.key, getCurrentHue(metric.key), parseInt(e.target.value))}
                                      className="w-full cursor-pointer"
                                    />
                                  </div>
                                </div>
                                <div className="flex gap-1">
                                  {[0, 1, 2, 3, 4].map((level) => {
                                    const localPalette = getLocalColorPalette(metric.key);
                                    const customColor = localPalette?.[level] || colorPreferences?.[metric.key]?.[level.toString()];
                                    return (
                                      <div
                                        key={level}
                                        className="flex-1 h-8 rounded"
                                        style={{
                                          backgroundColor:
                                            customColor || defaultColors[level as keyof typeof defaultColors],
                                        }}
                                      />
                                    );
                                  })}
                                </div>
                                <PresetButtons metricKey={metric.key} />
                                <ScalingButtons metricKey={metric.key} />
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => resetMetricColors(metric.key)}
                                  className="w-full text-xs"
                                >{t('ui.text.reset_to_default')}</Button>
                              </div>
                            </PopoverContent>
                          </Popover>
                        )}
                        <ViewModeButton metricKey={metric.key} />
                        {isOwnProfile && (
                          <>
                            {activeIndex > 0 && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-4 w-4 p-0 hover:bg-transparent"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  moveChart(activeIndex, "up");
                                }}
                              >
                                <ArrowUp className="w-3 h-3 text-muted-foreground hover:text-foreground" />
                              </Button>
                            )}
                            {activeIndex < activeMetrics.length - 1 && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-4 w-4 p-0 hover:bg-transparent"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  moveChart(activeIndex, "down");
                                }}
                              >
                                <ArrowDown className="w-3 h-3 text-muted-foreground hover:text-foreground" />
                              </Button>
                            )}
                          </>
                        )}
                      </>
                    ) : metric.key === "github_commits" ? (
                      /* GitHub Commits with last synced date */
                      <>
                        <div className="flex items-center flex-wrap gap-x-2 gap-y-1">
                          {isBarMode(metric.key) && (
                          <div className="flex items-center gap-1">
                            <span className="text-muted-foreground">0{shouldShowUnit(metric.label, metric.unit) ? metric.unit : ''}</span>
                            <div className="flex gap-1">
                              {[0, 1, 2, 3, 4].map((level) => {
                                const customColor = colorPreferences?.[metric.key]?.[level.toString()];
                                return (
                                  <div
                                    key={level}
                                    className={customColor ? "" : getSquareColor(level, false, metric.key)}
                                    style={
                                      customColor
                                        ? { backgroundColor: customColor, width: "12px", height: "12px", borderRadius: "2px" }
                                        : {
                                            backgroundColor: defaultColors[level as keyof typeof defaultColors],
                                            width: "12px",
                                            height: "12px",
                                            borderRadius: "2px",
                                          }
                                    }
                                  />
                                );
                              })}
                            </div>
                          </div>
                          )}
                          {!metric.isRelative && isBarMode(metric.key) && (
                          <div className="flex items-center gap-1">
                            <span className="text-muted-foreground">
                              {metric.goal}
                              {shouldShowUnit(metric.label, metric.unit) ? metric.unit : ''}
                            </span>
                            {isOwnProfile && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-4 w-4 p-0 hover:bg-transparent"
                                onClick={() => handleEditGoal(metric.key, metric.goal, metric.key === "calories" ? userGoals?.calories_inverse !== false : undefined)}
                              >
                                <Pencil className="w-3 h-3 text-muted-foreground hover:text-foreground" />
                              </Button>
                            )}
                          </div>
                          )}
                          {isOwnProfile && (
                          <Popover onOpenChange={(open) => handleColorPickerOpenChange(open, metric.key)}>
                                <PopoverTrigger asChild>
                                  <Button variant="ghost" size="sm" className="h-4 w-4 p-0 hover:bg-transparent">
                                    <Palette className="w-3 h-3 text-muted-foreground hover:text-foreground" />
                                  </Button>
                                </PopoverTrigger>
                                <PopoverContent className="w-64 bg-popover p-3" align="start">
                                  <div className="space-y-3">
                                    <div className="text-xs font-semibold">{t('ui.text.customize_color')}</div>
                                    <div className="space-y-2">
                                      <div className="text-xs text-muted-foreground">{t('ui.text.choose_a_hue_for_your_custom_gradient')}</div>
                                      <div className="relative">
                                        <div 
                                          className="w-full h-8 rounded mb-2"
                                          style={{
                                            background: 'linear-gradient(to right, hsl(0, 100%, 50%), hsl(60, 100%, 50%), hsl(120, 100%, 50%), hsl(180, 100%, 50%), hsl(240, 100%, 50%), hsl(300, 100%, 50%), hsl(360, 100%, 50%))'
                                          }}
                                        />
                                        <input
                                          type="range"
                                          min="0"
                                          max="360"
                                          value={getCurrentHue(metric.key)}
                                          onChange={(e) => handleColorChange(metric.key, parseInt(e.target.value), getCurrentSaturation(metric.key))}
                                          className="w-full cursor-pointer"
                                        />
                                      </div>
                                      <div className="relative mt-2">
                                        <div 
                                          className="w-full h-6 rounded mb-2"
                                          style={{
                                            background: `linear-gradient(to right, white, hsl(${getCurrentHue(metric.key)}, 100%, 50%))`
                                          }}
                                        />
                                        <input
                                          type="range"
                                          min="0"
                                          max="100"
                                          value={getCurrentSaturation(metric.key)}
                                          onChange={(e) => handleColorChange(metric.key, getCurrentHue(metric.key), parseInt(e.target.value))}
                                          className="w-full cursor-pointer"
                                        />
                                      </div>
                                    </div>
                                    <div className="flex gap-1">
                                      {[0, 1, 2, 3, 4].map((level) => {
                                        const localPalette = getLocalColorPalette(metric.key);
                                        const customColor = localPalette?.[level] || colorPreferences?.[metric.key]?.[level.toString()];
                                        return (
                                          <div
                                            key={level}
                                            className="flex-1 h-8 rounded"
                                            style={{
                                              backgroundColor:
                                                customColor || defaultColors[level as keyof typeof defaultColors],
                                            }}
                                          />
                                        );
                                      })}
                                    </div>
                                    <PresetButtons metricKey={metric.key} />
                                    <ScalingButtons metricKey={metric.key} />
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => resetMetricColors(metric.key)}
                                      className="w-full text-xs"
                                    >{t('ui.text.reset_to_default')}</Button>
                                  </div>
                                </PopoverContent>
                              </Popover>
                            )}
                        <ViewModeButton metricKey={metric.key} />
                        {isOwnProfile && (
                          <>
                            {activeIndex > 0 && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-4 w-4 p-0 hover:bg-transparent"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  moveChart(activeIndex, "up");
                                }}
                              >
                                <ArrowUp className="w-3 h-3 text-muted-foreground hover:text-foreground" />
                              </Button>
                            )}
                            {activeIndex < activeMetrics.length - 1 && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-4 w-4 p-0 hover:bg-transparent"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  moveChart(activeIndex, "down");
                                }}
                              >
                                <ArrowDown className="w-3 h-3 text-muted-foreground hover:text-foreground" />
                              </Button>
                            )}
                          </>
                          )}
                        </div>
                        {githubConnection?.last_synced_at && (
                          <div className="text-[10px] text-muted-foreground/60 mt-1">
                            Last synced: {format(new Date(githubConnection.last_synced_at), "MMM d, yyyy")}
                          </div>
                        )}
                      </>
                    ) : metric.key === "mood" || metric.key === "self_appearance_rating" ? (
                      /* Mood and Appearance - rating metrics without editable goals */
                      <>
                        {isBarMode(metric.key) && (
                        <div className="flex items-center gap-1">
                          <span className="text-muted-foreground">0{shouldShowUnit(metric.label, metric.unit) ? metric.unit : ''}</span>
                          <div className="flex gap-1">
                            {[0, 1, 2, 3, 4].map((level) => {
                              const customColor = colorPreferences?.[metric.key]?.[level.toString()];
                              return (
                                <div
                                  key={level}
                                  className={customColor ? "" : getSquareColor(level, false, metric.key)}
                                  style={
                                    customColor
                                      ? {
                                          backgroundColor: customColor,
                                          width: "12px",
                                          height: "12px",
                                          borderRadius: "2px",
                                        }
                                      : {
                                          backgroundColor: defaultColors[level as keyof typeof defaultColors],
                                          width: "12px",
                                          height: "12px",
                                          borderRadius: "2px",
                                        }
                                  }
                                />
                              );
                            })}
                          </div>
                          {!metric.isRelative && (
                          <span className="text-muted-foreground">
                            {metric.goal}
                            {shouldShowUnit(metric.label, metric.unit) ? metric.unit : ''}
                          </span>
                          )}
                        </div>
                        )}
                        {isOwnProfile && (
                          <Popover onOpenChange={(open) => handleColorPickerOpenChange(open, metric.key)}>
                            <PopoverTrigger asChild>
                              <Button variant="ghost" size="sm" className="h-4 w-4 p-0 hover:bg-transparent ml-1">
                                <Palette className="w-3 h-3 text-muted-foreground hover:text-foreground" />
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-64 bg-popover p-3" align="start">
                              <div className="space-y-3">
                                <div className="text-xs font-semibold">{t('ui.text.customize_color')}</div>
                                <div className="space-y-2">
                                  <div className="text-xs text-muted-foreground">{t('ui.text.choose_a_hue_for_your_custom_gradient')}</div>
                                  <div className="relative">
                                    <div 
                                      className="w-full h-8 rounded mb-2"
                                      style={{
                                        background: 'linear-gradient(to right, hsl(0, 100%, 50%), hsl(60, 100%, 50%), hsl(120, 100%, 50%), hsl(180, 100%, 50%), hsl(240, 100%, 50%), hsl(300, 100%, 50%), hsl(360, 100%, 50%))'
                                      }}
                                    />
                                    <input
                                      type="range"
                                      min="0"
                                      max="360"
                                      value={getCurrentHue(metric.key)}
                                      onChange={(e) => handleColorChange(metric.key, parseInt(e.target.value), getCurrentSaturation(metric.key))}
                                      className="w-full cursor-pointer"
                                    />
                                  </div>
                                  <div className="relative mt-2">
                                    <div 
                                      className="w-full h-6 rounded mb-2"
                                      style={{
                                        background: `linear-gradient(to right, white, hsl(${getCurrentHue(metric.key)}, 100%, 50%))`
                                      }}
                                    />
                                    <input
                                      type="range"
                                      min="0"
                                      max="100"
                                      value={getCurrentSaturation(metric.key)}
                                      onChange={(e) => handleColorChange(metric.key, getCurrentHue(metric.key), parseInt(e.target.value))}
                                      className="w-full cursor-pointer"
                                    />
                                  </div>
                                </div>
                                <div className="flex gap-1">
                                  {[0, 1, 2, 3, 4].map((level) => {
                                    const localPalette = getLocalColorPalette(metric.key);
                                    const customColor = localPalette?.[level] || colorPreferences?.[metric.key]?.[level.toString()];
                                    return (
                                      <div
                                        key={level}
                                        className="flex-1 h-8 rounded"
                                        style={{
                                          backgroundColor:
                                            customColor || defaultColors[level as keyof typeof defaultColors],
                                        }}
                                      />
                                    );
                                  })}
                                </div>
                                <PresetButtons metricKey={metric.key} />
                                <ScalingButtons metricKey={metric.key} />
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => resetMetricColors(metric.key)}
                                  className="w-full text-xs"
                                >{t('ui.text.reset_to_default')}</Button>
                              </div>
                            </PopoverContent>
                          </Popover>
                        )}
                        <ViewModeButton metricKey={metric.key} />
                        {isOwnProfile && (
                          <>
                            {activeIndex > 0 && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-4 w-4 p-0 hover:bg-transparent"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  moveChart(activeIndex, "up");
                                }}
                              >
                                <ArrowUp className="w-3 h-3 text-muted-foreground hover:text-foreground" />
                              </Button>
                            )}
                            {activeIndex < activeMetrics.length - 1 && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-4 w-4 p-0 hover:bg-transparent"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  moveChart(activeIndex, "down");
                                }}
                              >
                                <ArrowDown className="w-3 h-3 text-muted-foreground hover:text-foreground" />
                              </Button>
                            )}
                          </>
                        )}
                      </>
                    ) : metric.key === "body_fat_percentage" ? (
                      /* Body fat - closeness to goal is better */
                      <>
                        <span className="text-muted-foreground">&gt;10% off</span>
                        <div className="flex gap-1">
                          {[0, 1, 2, 3, 4].map((level) => {
                            const customColor = colorPreferences?.[metric.key]?.[level.toString()];
                            return (
                              <div
                                key={level}
                                className={customColor ? "" : getSquareColor(level, false, metric.key)}
                                style={
                                  customColor
                                    ? {
                                        backgroundColor: customColor,
                                        width: "12px",
                                        height: "12px",
                                        borderRadius: "2px",
                                      }
                                    : {
                                        backgroundColor: defaultColors[level as keyof typeof defaultColors],
                                        width: "12px",
                                        height: "12px",
                                        borderRadius: "2px",
                                      }
                                }
                              />
                            );
                          })}
                        </div>
                        <div className="flex items-center gap-1">
                          <span className="text-muted-foreground">&lt;1% off</span>
                          {isOwnProfile && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-4 w-4 p-0 hover:bg-transparent"
                              onClick={() => handleEditGoal(metric.key, metric.goal, metric.key === "calories" ? userGoals?.calories_inverse !== false : undefined)}
                            >
                              <Pencil className="w-3 h-3 text-muted-foreground hover:text-foreground" />
                            </Button>
                          )}
                          {isOwnProfile && (
                            <Popover onOpenChange={(open) => handleColorPickerOpenChange(open, metric.key)}>
                              <PopoverTrigger asChild>
                                <Button variant="ghost" size="sm" className="h-4 w-4 p-0 hover:bg-transparent">
                                  <Palette className="w-3 h-3 text-muted-foreground hover:text-foreground" />
                                </Button>
                              </PopoverTrigger>
                              <PopoverContent className="w-64 bg-popover p-3" align="start">
                                <div className="space-y-3">
                                  <div className="text-xs font-semibold">{t('ui.text.customize_color')}</div>
                                  <div className="space-y-2">
                                    <div className="text-xs text-muted-foreground">{t('ui.text.choose_a_hue_for_your_custom_gradient')}</div>
                                    <div className="relative">
                                      <div 
                                        className="w-full h-8 rounded mb-2"
                                        style={{
                                          background: 'linear-gradient(to right, hsl(0, 100%, 50%), hsl(60, 100%, 50%), hsl(120, 100%, 50%), hsl(180, 100%, 50%), hsl(240, 100%, 50%), hsl(300, 100%, 50%), hsl(360, 100%, 50%))'
                                        }}
                                      />
                                      <input
                                        type="range"
                                        min="0"
                                        max="360"
                                        value={getCurrentHue(metric.key)}
                                        onChange={(e) => handleColorChange(metric.key, parseInt(e.target.value), getCurrentSaturation(metric.key))}
                                        className="w-full cursor-pointer"
                                      />
                                    </div>
                                    <div className="relative mt-2">
                                      <div 
                                        className="w-full h-6 rounded mb-2"
                                        style={{
                                          background: `linear-gradient(to right, white, hsl(${getCurrentHue(metric.key)}, 100%, 50%))`
                                        }}
                                      />
                                      <input
                                        type="range"
                                        min="0"
                                        max="100"
                                        value={getCurrentSaturation(metric.key)}
                                        onChange={(e) => handleColorChange(metric.key, getCurrentHue(metric.key), parseInt(e.target.value))}
                                        className="w-full cursor-pointer"
                                      />
                                    </div>
                                  </div>
                                  <div className="flex gap-1">
                                    {[0, 1, 2, 3, 4].map((level) => {
                                      const localPalette = getLocalColorPalette(metric.key);
                                      const customColor = localPalette?.[level] || colorPreferences?.[metric.key]?.[level.toString()];
                                      return (
                                        <div
                                          key={level}
                                          className="flex-1 h-8 rounded"
                                          style={{
                                            backgroundColor:
                                              customColor || defaultColors[level as keyof typeof defaultColors],
                                          }}
                                        />
                                      );
                                    })}
                                  </div>
                                  <PresetButtons metricKey={metric.key} />
                                  <ScalingButtons metricKey={metric.key} />
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => resetMetricColors(metric.key)}
                                    className="w-full text-xs"
                                  >{t('ui.text.reset_to_default')}</Button>
                                </div>
                              </PopoverContent>
                            </Popover>
                          )}
                          <ViewModeButton metricKey={metric.key} />
                          {isOwnProfile && (
                            <>
                              {activeIndex > 0 && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-4 w-4 p-0 hover:bg-transparent"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    moveChart(activeIndex, "up");
                                  }}
                                >
                                  <ArrowUp className="w-3 h-3 text-muted-foreground hover:text-foreground" />
                                </Button>
                              )}
                              {activeIndex < activeMetrics.length - 1 && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-4 w-4 p-0 hover:bg-transparent"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    moveChart(activeIndex, "down");
                                  }}
                                >
                                  <ArrowDown className="w-3 h-3 text-muted-foreground hover:text-foreground" />
                                </Button>
                              )}
                            </>
                          )}
                        </div>
                      </>
                    ) : (
                      /* Normal metrics - higher is better */
                      <>
                        {isBarMode(metric.key) && (
                          <div className="flex items-center gap-1">
                            <span className="text-muted-foreground">0{shouldShowUnit(metric.label, metric.unit) ? metric.unit : ''}</span>
                            <div className="flex gap-1">
                              {[0, 1, 2, 3, 4].map((level) => {
                                const customColor = colorPreferences?.[metric.key]?.[level.toString()];
                                return (
                                  <div
                                    key={level}
                                    className={customColor ? "" : getSquareColor(level, false, metric.key)}
                                    style={
                                      customColor
                                        ? {
                                            backgroundColor: customColor,
                                            width: "12px",
                                            height: "12px",
                                            borderRadius: "2px",
                                          }
                                        : {
                                            backgroundColor: defaultColors[level as keyof typeof defaultColors],
                                            width: "12px",
                                            height: "12px",
                                            borderRadius: "2px",
                                          }
                                    }
                                  />
                                );
                              })}
                            </div>
                          </div>
                        )}
                        {!metric.isRelative && isBarMode(metric.key) && (
                          <div className="flex items-center gap-1">
                          <span className="text-muted-foreground">
                            {metric.goal}
                            {shouldShowUnit(metric.label, metric.unit) ? metric.unit : ''}
                          </span>
                          {isOwnProfile && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-4 w-4 p-0 hover:bg-transparent"
                              onClick={() => handleEditGoal(metric.key, metric.goal, metric.key === "calories" ? userGoals?.calories_inverse !== false : undefined)}
                            >
                              <Pencil className="w-3 h-3 text-muted-foreground hover:text-foreground" />
                            </Button>
                          )}
                          </div>
                        )}
                        {/* Control buttons - always visible */}
                        <div className="flex items-center gap-1 flex-shrink-0">
                          {isOwnProfile && (
                            <Popover onOpenChange={(open) => handleColorPickerOpenChange(open, metric.key)}>
                              <PopoverTrigger asChild>
                                <Button variant="ghost" size="sm" className="h-4 w-4 p-0 hover:bg-transparent">
                                  <Palette className="w-3 h-3 text-muted-foreground hover:text-foreground" />
                                </Button>
                              </PopoverTrigger>
                              <PopoverContent className="w-64 bg-popover p-3" align="start">
                                <div className="space-y-3">
                                  <div className="text-xs font-semibold">{t('ui.text.customize_color')}</div>
                                  <div className="space-y-2">
                                    <div className="text-xs text-muted-foreground">{t('ui.text.choose_a_hue_for_your_custom_gradient')}</div>
                                    <div className="relative">
                                      <div 
                                        className="w-full h-8 rounded mb-2"
                                        style={{
                                          background: 'linear-gradient(to right, hsl(0, 100%, 50%), hsl(60, 100%, 50%), hsl(120, 100%, 50%), hsl(180, 100%, 50%), hsl(240, 100%, 50%), hsl(300, 100%, 50%), hsl(360, 100%, 50%))'
                                        }}
                                      />
                                      <input
                                        type="range"
                                        min="0"
                                        max="360"
                                        value={getCurrentHue(metric.key)}
                                        onChange={(e) => handleColorChange(metric.key, parseInt(e.target.value), getCurrentSaturation(metric.key))}
                                        className="w-full cursor-pointer"
                                      />
                                    </div>
                                    <div className="relative mt-2">
                                      <div 
                                        className="w-full h-6 rounded mb-2"
                                        style={{
                                          background: `linear-gradient(to right, white, hsl(${getCurrentHue(metric.key)}, 100%, 50%))`
                                        }}
                                      />
                                      <input
                                        type="range"
                                        min="0"
                                        max="100"
                                        value={getCurrentSaturation(metric.key)}
                                        onChange={(e) => handleColorChange(metric.key, getCurrentHue(metric.key), parseInt(e.target.value))}
                                        className="w-full cursor-pointer"
                                      />
                                    </div>
                                  </div>
                                  <div className="flex gap-1">
                                    {[0, 1, 2, 3, 4].map((level) => {
                                      const localPalette = getLocalColorPalette(metric.key);
                                      const customColor = localPalette?.[level] || colorPreferences?.[metric.key]?.[level.toString()];
                                      return (
                                        <div
                                          key={level}
                                          className="flex-1 h-8 rounded"
                                          style={{
                                            backgroundColor:
                                              customColor || defaultColors[level as keyof typeof defaultColors],
                                          }}
                                        />
                                      );
                                    })}
                                  </div>
                                  <PresetButtons metricKey={metric.key} />
                                  <ScalingButtons metricKey={metric.key} />
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => resetMetricColors(metric.key)}
                                    className="w-full text-xs"
                                  >{t('ui.text.reset_to_default')}</Button>
                                </div>
                              </PopoverContent>
                            </Popover>
                          )}
                          <ViewModeButton metricKey={metric.key} />
                          {isOwnProfile && (
                            <>
                              {activeIndex > 0 && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-4 w-4 p-0 hover:bg-transparent"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    moveChart(activeIndex, "up");
                                  }}
                                >
                                  <ArrowUp className="w-3 h-3 text-muted-foreground hover:text-foreground" />
                                </Button>
                              )}
                              {activeIndex < activeMetrics.length - 1 && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-4 w-4 p-0 hover:bg-transparent"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    moveChart(activeIndex, "down");
                                  }}
                                >
                                  <ArrowDown className="w-3 h-3 text-muted-foreground hover:text-foreground" />
                                </Button>
                              )}
                            </>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                  {currentViewerId && (
                    <GraphReactions 
                      graphOwnerId={userId} 
                      currentUserId={currentViewerId}
                      metricKey={metric.key}
                      readOnly={currentViewerId === userId}
                    />
                  )}
                </div>
              );
            })
          )}
        </div>
        
        {/* Desktop: Year navigator on the right side (hidden in condensed/month/compact views) */}
        {!isMobile && (condensedViewMode === 'full' || condensedViewMode === 'compactGrid') && (
          <div className="flex flex-col gap-2 items-end mr-10">{yearNavigator}</div>
        )}
      </div>



        {/* Goal editing dialog */}
        <Dialog open={!!editingGoal} onOpenChange={(open) => !open && setEditingGoal(null)}>
          <DialogContent
            aria-describedby={undefined}
            onEscapeKeyDown={(e) => e.preventDefault()}
            onPointerDownOutside={(e) => e.preventDefault()}
            onInteractOutside={(e) => e.preventDefault()}
          >
            <DialogHeader>
              <DialogTitle>{t('ui.text.edit_goal')}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="goal-value">
                  {editingGoal && allMetrics.find((m) => m.key === editingGoal.metricKey)?.label} Goal
                </Label>
                <Input
                  id="goal-value"
                  type="number"
                  value={goalValue}
                  onChange={(e) => setGoalValue(e.target.value)}
                  placeholder={t('forms.placeholders.enter_goal_value')}
                />
              </div>
              {/* Show "Above is bad" checkbox for calories */}
              {editingGoal?.metricKey === "calories" && (
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="calories-inverse-chart"
                    checked={editingGoal?.isInverse !== false}
                    onCheckedChange={(checked) => 
                      setEditingGoal(editingGoal ? { ...editingGoal, isInverse: checked === true } : null)
                    }
                  />
                  <Label htmlFor="calories-inverse-chart" className="text-sm font-normal cursor-pointer">
                    {t('ui.text.above_is_bad')}
                  </Label>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditingGoal(null)}>
                Cancel
              </Button>
              <Button onClick={handleSaveGoal} disabled={updateGoal.isPending}>
                Save
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Progress Snap Viewer */}
        <ProgressSnapViewer
          key={selectedSnap?.id || 'closed'}
          snap={selectedSnap}
          onClose={() => {
            setSelectedSnap(null);
            setSameDateSnaps([]);
          }}
          currentUserId={currentViewerId || currentUserId || undefined}
          metricEmoji={selectedSnap ? displayMetrics.find(m => m.key === selectedSnap.metric_name)?.emoji : undefined}
          metricLabel={selectedSnap ? displayMetrics.find(m => m.key === selectedSnap.metric_name)?.label : undefined}
          onDelete={() => {
            queryClient.invalidateQueries({ queryKey: ["progress-snaps"] });
          }}
          sameDateSnaps={sameDateSnaps}
        />

        {/* Daily Entries Modal */}
        <DailyEntriesModal
          open={dailyEntriesModalOpen && selectedDayForEntries !== null}
          onOpenChange={setDailyEntriesModalOpen}
          userId={userId}
          currentUserId={currentViewerId}
          entryDate={selectedDayForEntries?.date || ""}
          metricKey={selectedDayForEntries?.metricKey || ""}
          metricName={selectedDayForEntries?.metricName || ""}
          metricEmoji={selectedDayForEntries?.metricEmoji || ""}
          metricUnit={selectedDayForEntries?.metricUnit || ""}
          isBoolean={selectedDayForEntries?.isBoolean || false}
        />
      </div>
    );
  }

  return (
    <div className="w-full space-y-6">
      <div className="hidden md:block">{filtersSection}</div>

      {isLoadingEntries ? (
        // Loading skeleton for charts - full year view always for non-chartOnly
        <div className="space-y-6">
          {[1, 2, 3].map((i) => (
            <div key={i} className="space-y-2">
              <div className="h-6 w-32 bg-muted animate-pulse rounded"></div>
              <div className="h-24 w-full bg-muted animate-pulse rounded"></div>
            </div>
          ))}
        </div>
      ) : (
        <>
          {activeMetrics
            .map((metricKey) => displayMetrics.find((m) => m.key === metricKey))
            .filter((metric): metric is typeof displayMetrics[0] => metric !== undefined)
            .map((metric, activeIndex) => {
              // Calculate if this metric has an active streak
              const onStreak = hasActiveStreak(metric.key, entries, metric, userGoals, customMetrics);
              
              // Get the chart color for the aura (from custom colors or default)
              const customColors = colorPreferences?.[metric.key];
              const chartColor = customColors?.['4'] || defaultColors[4]; // Use highest intensity color
              
              return (
              <div 
                key={metric.key} 
                className="space-y-2 relative"
                data-chart-index={activeIndex}
                data-chart-metric={metric.key}
              >
                {/* Streak aura effect */}
                {onStreak && (
                  <>
                    <div 
                      className="streak-aura"
                      style={{ 
                        background: `radial-gradient(ellipse at center, ${chartColor}40 0%, ${chartColor}20 40%, transparent 70%)`,
                      }}
                    />
                    <div 
                      className="streak-aura"
                      style={{ 
                        background: `radial-gradient(ellipse at center, ${chartColor}30 0%, transparent 60%)`,
                      }}
                    />
                  </>
                )}
                
                <div className="flex items-center gap-2 relative z-10">
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (isOwnProfile) {
                          openQuickEditForMetric(metric.key);
                        }
                      }}
                      disabled={!isOwnProfile}
                      className="text-left text-lg font-semibold inline-flex items-center gap-1 group focus:outline-none touch-manipulation cursor-pointer active:opacity-70 disabled:cursor-default disabled:opacity-100 min-h-[44px] -my-2 py-2"
                      aria-label={`Open quick edit for ${metric.label}`}
                    >
                      <span>
                        {metric.emoji} {metric.label}
                      </span>
                      {isOwnProfile && (
                        <span
                          aria-hidden="true"
                          className="ml-0.5 text-muted-foreground/40 group-hover:text-muted-foreground/60 group-active:text-muted-foreground/60 font-light"
                          style={{ transform: "translateY(1px)" }}
                        >
                          &gt;
                        </span>
                      )}
                    </button>
                    {/* Screenshot mode: Dice button to randomize this graph's data */}
                    {showDiceButtons && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          console.log('[Screenshot] Dice clicked (2nd location), metric object:', {
                            key: metric.key,
                            isBoolean: (metric as any).isBoolean,
                            metric_type: metric.metric_type,
                            isInverse: (metric as any).isInverse,
                            is_inverse: (metric as any).is_inverse,
                          });
                          randomizeMetricData(
                            metric.key, 
                            metric.goal || 100, 
                            (metric as any).isInverse || (metric as any).is_inverse || false,
                            (metric as any).isBoolean || metric.metric_type === "boolean"
                          );
                        }}
                        className="p-1.5 rounded-md bg-primary/10 hover:bg-primary/20 text-primary transition-colors"
                      >
                        <Dices className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
                <div className="relative flex gap-1 items-center z-10">
                  {/* Y-axis labels - conditional based on view mode */}
                  {isBarMode(metric.key) ? (
                    <div className="flex flex-col gap-1 pr-1">
                      <div className="h-3 text-[9px] text-muted-foreground/50 flex items-center"></div>
                      <div className="h-3 text-[9px] text-muted-foreground/50 flex items-center"></div>
                      <div className="h-3 text-[9px] text-muted-foreground/50 flex items-center text-right relative" style={{ top: '-5px' }}>Mon</div>
                      <div className="h-3 text-[9px] text-muted-foreground/50 flex items-center"></div>
                      <div className="h-3 text-[9px] text-muted-foreground/50 flex items-center text-right relative" style={{ top: '-4px' }}>Wed</div>
                      <div className="h-3 text-[9px] text-muted-foreground/50 flex items-center"></div>
                      <div className="h-3 text-[9px] text-muted-foreground/50 flex items-center text-right relative" style={{ top: '-2px' }}>Fri</div>
                      <div className="h-3 text-[9px] text-muted-foreground/50 flex items-center"></div>
                    </div>
                  ) : (
                    <div className="flex flex-col justify-between pr-1" style={{ height: '75px' }}>
                      {(() => {
                        const currentMode = getViewMode(metric.key);
                        if (!currentMode || currentMode === 'bar') return null;
                        
                        const dataPoints = processLineChartData(weeks, metric, currentMode);
                        const maxValue = Math.max(...dataPoints.map(d => d.y), 1);
                        const steps = 4;
                        
                        // Generate labels with duplicate prevention
                        const labels: number[] = [];
                        const stepValue = maxValue / steps;
                        
                        for (let i = 0; i <= steps; i++) {
                          const value = maxValue - (i * stepValue);
                          // Only add if different enough from last label (5% threshold)
                          if (labels.length === 0 || Math.abs(value - labels[labels.length - 1]) > maxValue * 0.05) {
                            labels.push(value);
                          }
                        }
                        
                        // Ensure at least 2 labels (max and min)
                        if (labels.length < 2) {
                          labels.length = 0;
                          labels.push(maxValue, 0);
                        }
                        
                        // Pad to 5 labels for consistent spacing
                        while (labels.length < 5) {
                          labels.push(labels[labels.length - 1]);
                        }
                        
                        return labels.map((value, i) => (
                          <div key={i} className="h-3 text-[9px] text-muted-foreground/50 flex items-center text-right pr-1">
                            {value < 10 ? value.toFixed(1) : Math.round(value)}
                          </div>
                        ));
                      })()}
                    </div>
                  )}
                  
                  {/* Chart area - conditional rendering */}
                  <div className="flex-1 overflow-x-hidden overflow-y-hidden pb-2.5">
                    {isBarMode(metric.key) ? (
                      // Bar chart (original grid view)
                      <div className="flex gap-1">
                        {weeks.map((week, weekIndex) => (
                          <div key={weekIndex} className="flex w-3 flex-col gap-1 items-center flex-none">
                            <div className="w-3 h-3 text-[9px] text-muted-foreground/50 text-center leading-3">
                              {getMonthLabel(weekIndex)}
                            </div>
                            {week.map((day, dayIndex) => {
                              if (!day) {
                                // Empty square for dates outside the year
                                return <div key={dayIndex} className="w-3 h-3" />;
                              }
                              const rawIntensity = metric.getValue(day.entry);
                              const dateStr = format(day.date, "yyyy-MM-dd");
                              const intensity = getDisplayIntensity(metric.key, dateStr, rawIntensity);
                              const isPenalized = penalizedMetrics.get(dateStr)?.has(metric.key) || false;
                              const upcomingCharge = willBeCharged(dateStr, metric.key, day.entry);
                              const customColors = colorPreferences?.[metric.key];
                              
                              // Determine background color based on intensity
                              let bgColor = "";
                              let bgStyle: { backgroundColor?: string } | undefined = undefined;
                              
                              if (isPenalized) {
                                bgColor = "bg-red-600";
                              } else if (customColors?.[intensity.toString()]) {
                                // Use inline style for custom colors (Tailwind JIT can't see dynamic classes)
                                bgStyle = { backgroundColor: customColors[intensity.toString()] };
                              } else {
                                bgColor = getSquareColor(intensity, false, metric.key);
                                // Use red colors for negative intensities (calorie overshoot), green for positive
                                if (intensity < 0) {
                                  bgStyle = { backgroundColor: redColors[intensity.toString() as keyof typeof redColors] };
                                } else {
                                  bgStyle = { backgroundColor: defaultColors[intensity as keyof typeof defaultColors] };
                                }
                              }

                              return (
                                <div
                                  key={dayIndex}
                                  className="relative cursor-pointer"
                                  onClick={() => handleDayClick(day.date, day.entry, metric)}
                                  style={{
                                    // Larger touch target for mobile
                                    padding: isMobile ? '3px' : '0',
                                    margin: isMobile ? '-3px' : '0',
                                    touchAction: 'manipulation', // Prevent double-tap zoom
                                  }}
                                >
                                  {(() => { const scalingFY2 = getIntensityScaling(metric.key); const scaleFY2 = getSquareScale(intensity, scalingFY2); return (
                                  <div
                                    className={`w-3 h-3 rounded-sm select-none ${bgColor} ${onStreak ? 'streak-wave-square' : ''} hover:ring-2 hover:ring-primary transition-all`}
                                    style={{
                                      ...(bgStyle ||
                                        (customColors?.[intensity.toString()]
                                          ? { backgroundColor: customColors[intensity.toString()] }
                                          : undefined)),
                                      ...(onStreak ? { animationDelay: `${weekIndex * 0.05}s` } : {}),
                                      ...(scaleFY2 < 1 ? { scale: `${scaleFY2}`, transformOrigin: 'center' } as React.CSSProperties : {})
                                    }}
                                    title={`${format(day.date, "MMM d, yyyy")}${isPenalized ? " - Donation Processed" : upcomingCharge ? " - Upcoming Donation" : ` - Intensity: ${intensity}`}`}
                                  />
                                  ); })()}
                                  {upcomingCharge && !isPenalized && (
                                    <div 
                                      className="absolute inset-0 flex items-center justify-center pointer-events-none"
                                      style={{ fontSize: '8px', lineHeight: '12px' }}
                                    >
                                      ⚠️
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        ))}
                      </div>
                    ) : (
                      // Line chart view
                      <div className="relative" style={{ height: '75px' }}>
                        {/* Month labels */}
                        <div className="absolute top-0 left-0 right-0 flex">
                          {weeks.map((_, weekIndex) => (
                            <div key={weekIndex} className="flex-none text-[9px] text-muted-foreground/50 text-center" style={{ width: '12px' }}>
                              {getMonthLabel(weekIndex)}
                            </div>
                          ))}
                        </div>
                        
                        {/* Camera icons overlay - positioned absolutely above chart */}
                        {(() => {
                          const currentMode = getViewMode(metric.key);
                          if (!currentMode || currentMode === 'bar') return null;
                          
                          const dataPoints = processLineChartData(weeks, metric, currentMode);
                          if (dataPoints.length === 0) return null;
                          
                          const maxValue = Math.max(...dataPoints.map(d => d.y), 1);
                          const totalWidth = weeks.length * 7 * 3;
                          
                          return dataPoints.filter(p => p.hasSnap).map((point, i) => {
                            const xPercent = (point.x * 3 / totalWidth) * 100;
                            const cy = 63 - (point.y / maxValue) * 63;
                            const yPixels = 12 + cy; // 12px offset from top + cy position
                            // Get the first snap date for initial display
                            const firstSnapDate = point.snapDates && point.snapDates.length > 0 ? point.snapDates[0] : format(point.date, "yyyy-MM-dd");
                            const snapKey = `${firstSnapDate}_${metric.key}`;
                            const snap = progressSnapsByDateMetric.get(snapKey);
                            
                            // Check if camera would overflow top - if so, position below
                            const wouldOverflowTop = (yPixels - 32) < 0;
                            const iconTop = wouldOverflowTop ? yPixels + 8 : yPixels - 32;
                            const lineStyle = wouldOverflowTop 
                              ? { width: '1px', height: '8px', bottom: '20px', transform: 'translateX(-50%)' }
                              : { width: '1px', height: '26px', top: '10px', transform: 'translateX(-50%)' };
                            
                            return (
                              <div
                                key={`camera-${i}`}
                                className="absolute cursor-pointer z-20"
                                style={{
                                  left: `${xPercent}%`,
                                  top: `${iconTop}px`,
                                  transform: 'translateX(-50%)',
                                }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (snap && point.snapDates) {
                                    // Collect snaps for this metric from ALL dates in the week
                                    const snapsForMetric = Array.isArray(progressSnaps) 
                                      ? progressSnaps.filter((s: any) => 
                                          point.snapDates!.includes(s.snap_date) && s.metric_name === metric.key
                                        ).sort((a, b) => a.snap_date.localeCompare(b.snap_date) || a.created_at.localeCompare(b.created_at))
                                      : [];
                                    // Set the first snap in the sorted array as the selected snap
                                    setSelectedSnap(snapsForMetric.length > 0 ? snapsForMetric[0] : snap);
                                    setSameDateSnaps(snapsForMetric);
                                  }
                                }}
                              >
                                {/* Connecting line */}
                                <div 
                                  className="absolute left-1/2 bg-white/30 pointer-events-none"
                                  style={lineStyle}
                                />
                                <Camera className="w-5 h-5 text-black dark:text-white system:text-white relative z-10" strokeWidth={1.5} />
                              </div>
                            );
                          });
                        })()}
                        
                        {/* SVG line chart */}
                        <svg 
                          className="absolute" 
                          style={{ top: '12px', left: 0, width: '100%', height: '63px' }}
                          preserveAspectRatio="none"
                          viewBox={`0 0 ${weeks.length * 7 * 3} 63`}
                        >
                          {(() => {
                            const currentMode = getViewMode(metric.key);
                            if (!currentMode || currentMode === 'bar') return null;
                            
                            const dataPoints = processLineChartData(weeks, metric, currentMode);
                            if (dataPoints.length === 0) return null;
                            
                            const maxValue = Math.max(...dataPoints.map(d => d.y), 1);
                            const chartColor = getChartColor(metric.key);
                            
                            // Create path
                            const pathData = dataPoints.map((point, i) => {
                              const x = point.x * 3;
                              const y = 63 - (point.y / maxValue) * 63;
                              return `${i === 0 ? 'M' : 'L'} ${x} ${y}`;
                            }).join(' ');
                            
                            // Create area fill path
                            const areaPath = pathData + ` L ${dataPoints[dataPoints.length - 1].x * 3} 63 L 0 63 Z`;
                            const gradientId = `gradient-${metric.key}`;
                            
                            return (
                              <>
                                {/* Gradient definition */}
                                <defs>
                                  <linearGradient id={gradientId} x1="0%" y1="0%" x2="0%" y2="100%">
                                    <stop offset="0%" stopColor={chartColor} stopOpacity="0.1" />
                                    <stop offset="100%" stopColor={chartColor} stopOpacity="0" />
                                  </linearGradient>
                                </defs>
                                {/* Area fill with gradient */}
                                <path
                                  d={areaPath}
                                  fill={`url(#${gradientId})`}
                                />
                                {/* Bottom white line */}
                                <line
                                  x1="0"
                                  y1="63"
                                  x2={dataPoints[dataPoints.length - 1].x * 3}
                                  y2="63"
                                  stroke="white"
                                  strokeWidth="1"
                                  strokeOpacity="0.05"
                                />
                                {/* Line */}
                                <path
                                  d={pathData}
                                  stroke={chartColor}
                                  strokeWidth="1.5"
                                  fill="none"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                                {/* Data points */}
                                {dataPoints.map((point, i) => {
                                  const cx = point.x * 3;
                                  const cy = 63 - (point.y / maxValue) * 63;
                                  return (
                                    <circle
                                      key={i}
                                      cx={cx}
                                      cy={cy}
                                      r="1.5"
                                      fill={chartColor}
                                      className="cursor-pointer hover:r-2"
                                      onClick={() => {
                                        const day = weeks[Math.floor(point.x / 7)]?.[point.x % 7];
                                        if (day) handleDayClick(day.date, day.entry, metric);
                                      }}
                                    >
                                      <title>{`${format(point.date, "MMM d, yyyy")}: ${Math.round(point.y)}`}</title>
                                    </circle>
                                  );
                                })}
                              </>
                            );
                          })()}
                        </svg>
                      </div>
                    )}
                  </div>
                </div>
                {/* Legend and controls toolbar */}
                <div className={metric.key === "github_commits" ? "text-[10px] mt-2 ml-9" : "flex items-center flex-wrap gap-x-2 gap-y-1 text-[10px] mt-2 ml-9"}>
                    {/* Boolean/flag metrics or custom boolean metrics */}
                    {metric.key === "ate_healthy" || metric.key === "low_sugars" || metric.key === "fasted" || (metric as any).isBoolean ? (
                    <>
                      {isBarMode(metric.key) && (
                      <>
                      <span>{t('common.no')}</span>
                      <div className="flex gap-1">
                        {[0, 4].map((level) => {
                          // For inverse boolean metrics, always use default colors (red/green)
                          const useDefaultColors = (metric as any).isBoolean && (metric as any).isInverse;
                          const customColor = useDefaultColors ? null : colorPreferences?.[metric.key]?.[level.toString()];
                          
                          // For inverse boolean metrics, use red for "Yes" (level 4)
                          const backgroundColor = customColor 
                            ? customColor 
                            : (useDefaultColors && level === 4) 
                              ? redColors["-4"]
                              : defaultColors[level as keyof typeof defaultColors];
                          
                          return (
                            <div
                              key={level}
                              className={customColor ? "" : getSquareColor(level, false, metric.key)}
                              style={{
                                backgroundColor,
                                width: "12px",
                                height: "12px",
                                borderRadius: "2px",
                              }}
                            />
                          );
                        })}
                      </div>
                      <span>{t('common.yes')}</span>
                      </>
                      )}
                      {/* Hide color picker for inverse boolean metrics */}
                      {isOwnProfile && !((metric as any).isBoolean && (metric as any).isInverse) && (
                        <Popover onOpenChange={(open) => handleColorPickerOpenChange(open, metric.key)}>
                          <PopoverTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-4 w-4 p-0 hover:bg-transparent ml-1">
                              <Palette className="w-3 h-3 text-muted-foreground hover:text-foreground" />
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-64 bg-popover p-3" align="start">
                            <div className="space-y-3">
                              <div className="text-xs font-semibold">{t('ui.text.customize_color')}</div>
                              <div className="space-y-2">
                                <div className="text-xs text-muted-foreground">{t('ui.text.choose_a_hue_for_your_custom_gradient')}</div>
                                <div className="relative">
                                  <div 
                                    className="w-full h-8 rounded mb-2"
                                    style={{
                                      background: 'linear-gradient(to right, hsl(0, 100%, 50%), hsl(60, 100%, 50%), hsl(120, 100%, 50%), hsl(180, 100%, 50%), hsl(240, 100%, 50%), hsl(300, 100%, 50%), hsl(360, 100%, 50%))'
                                    }}
                                  />
                                  <input
                                    type="range"
                                    min="0"
                                    max="360"
                                    value={getCurrentHue(metric.key)}
                                    onChange={(e) => handleColorChange(metric.key, parseInt(e.target.value), getCurrentSaturation(metric.key))}
                                    className="w-full cursor-pointer"
                                  />
                                </div>
                                <div className="relative mt-2">
                                  <div 
                                    className="w-full h-6 rounded mb-2"
                                    style={{
                                      background: `linear-gradient(to right, white, hsl(${getCurrentHue(metric.key)}, 100%, 50%))`
                                    }}
                                  />
                                  <input
                                    type="range"
                                    min="0"
                                    max="100"
                                    value={getCurrentSaturation(metric.key)}
                                    onChange={(e) => handleColorChange(metric.key, getCurrentHue(metric.key), parseInt(e.target.value))}
                                    className="w-full cursor-pointer"
                                  />
                                </div>
                              </div>
                              <div className="flex gap-1">
                                {[0, 1, 2, 3, 4].map((level) => {
                                  const localPalette = getLocalColorPalette(metric.key);
                                  const customColor = localPalette?.[level] || colorPreferences?.[metric.key]?.[level.toString()];
                                  return (
                                    <div
                                      key={level}
                                      className="flex-1 h-8 rounded"
                                      style={{
                                        backgroundColor:
                                          customColor || defaultColors[level as keyof typeof defaultColors],
                                      }}
                                    />
                                  );
                                })}
                              </div>
                              <PresetButtons metricKey={metric.key} />
                              <ScalingButtons metricKey={metric.key} />
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => resetMetricColors(metric.key)}
                                className="w-full text-xs"
                              >{t('ui.text.reset_to_default')}</Button>
                            </div>
                          </PopoverContent>
                        </Popover>
                      )}
                      <ViewModeButton metricKey={metric.key} />
                      {isOwnProfile && (
                        <>
                          {activeIndex > 0 && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-4 w-4 p-0 hover:bg-transparent"
                              onClick={(e) => {
                                e.stopPropagation();
                                moveChart(activeIndex, "up");
                              }}
                            >
                              <ArrowUp className="w-3 h-3 text-muted-foreground hover:text-foreground" />
                            </Button>
                          )}
                          {activeIndex < activeMetrics.length - 1 && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-4 w-4 p-0 hover:bg-transparent"
                              onClick={(e) => {
                                e.stopPropagation();
                                moveChart(activeIndex, "down");
                              }}
                            >
                              <ArrowDown className="w-3 h-3 text-muted-foreground hover:text-foreground" />
                            </Button>
                          )}
                        </>
                      )}
                    </>
                  ) : metric.key === "github_commits" ? (
                    /* GitHub Commits with last synced date */
                    <>
                      <div className="flex items-center flex-wrap gap-x-2 gap-y-1">
                        {(isBarMode(metric.key)) && (
                        <>
                          <span className="text-muted-foreground">0{shouldShowUnit(metric.label, metric.unit) ? metric.unit : ''}</span>
                          <div className="flex gap-1">
                            {[0, 1, 2, 3, 4].map((level) => {
                              const customColor = colorPreferences?.[metric.key]?.[level.toString()];
                              return (
                                <div
                                  key={level}
                                  className={customColor ? "" : getSquareColor(level, false, metric.key)}
                                  style={
                                    customColor
                                      ? { backgroundColor: customColor, width: "12px", height: "12px", borderRadius: "2px" }
                                      : {
                                          backgroundColor: defaultColors[level as keyof typeof defaultColors],
                                          width: "12px",
                                          height: "12px",
                                          borderRadius: "2px",
                                        }
                                  }
                                />
                              );
                            })}
                          </div>
                        </>
                        )}
                        {!metric.isRelative && (isBarMode(metric.key)) && (
                        <>
                          <span className="text-muted-foreground">
                            {metric.goal}
                            {shouldShowUnit(metric.label, metric.unit) ? metric.unit : ''}
                          </span>
                          {isOwnProfile && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-4 w-4 p-0 hover:bg-transparent"
                              onClick={() => handleEditGoal(metric.key, metric.goal, metric.key === "calories" ? userGoals?.calories_inverse !== false : undefined)}
                            >
                              <Pencil className="w-3 h-3 text-muted-foreground hover:text-foreground" />
                            </Button>
                          )}
                        </>
                        )}
                        {isOwnProfile && (
                        <Popover onOpenChange={(open) => handleColorPickerOpenChange(open, metric.key)}>
                          <PopoverTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-4 w-4 p-0 hover:bg-transparent">
                              <Palette className="w-3 h-3 text-muted-foreground hover:text-foreground" />
                            </Button>
                          </PopoverTrigger>
                              <PopoverContent className="w-64 bg-popover p-3" align="start">
                                <div className="space-y-3">
                                  <div className="text-xs font-semibold">{t('ui.text.customize_color')}</div>
                                  <div className="space-y-2">
                                    <div className="text-xs text-muted-foreground">{t('ui.text.choose_a_hue_for_your_custom_gradient')}</div>
                                    <div className="relative">
                                      <div 
                                        className="w-full h-8 rounded mb-2"
                                        style={{
                                          background: 'linear-gradient(to right, hsl(0, 100%, 50%), hsl(60, 100%, 50%), hsl(120, 100%, 50%), hsl(180, 100%, 50%), hsl(240, 100%, 50%), hsl(300, 100%, 50%), hsl(360, 100%, 50%))'
                                        }}
                                      />
                                      <input
                                        type="range"
                                        min="0"
                                        max="360"
                                        value={getCurrentHue(metric.key)}
                                        onChange={(e) => handleColorChange(metric.key, parseInt(e.target.value), getCurrentSaturation(metric.key))}
                                        className="w-full cursor-pointer"
                                      />
                                    </div>
                                    <div className="relative mt-2">
                                      <div 
                                        className="w-full h-6 rounded mb-2"
                                        style={{
                                          background: `linear-gradient(to right, white, hsl(${getCurrentHue(metric.key)}, 100%, 50%))`
                                        }}
                                      />
                                      <input
                                        type="range"
                                        min="0"
                                        max="100"
                                        value={getCurrentSaturation(metric.key)}
                                        onChange={(e) => handleColorChange(metric.key, getCurrentHue(metric.key), parseInt(e.target.value))}
                                        className="w-full cursor-pointer"
                                      />
                                    </div>
                                  </div>
                                  <div className="flex gap-1">
                                    {[0, 1, 2, 3, 4].map((level) => {
                                      const localPalette = getLocalColorPalette(metric.key);
                                      const customColor = localPalette?.[level] || colorPreferences?.[metric.key]?.[level.toString()];
                                      return (
                                        <div
                                          key={level}
                                          className="flex-1 h-8 rounded"
                                          style={{
                                            backgroundColor:
                                              customColor || defaultColors[level as keyof typeof defaultColors],
                                          }}
                                        />
                                      );
                                    })}
                                  </div>
                                  <PresetButtons metricKey={metric.key} />
                                  <ScalingButtons metricKey={metric.key} />
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => resetMetricColors(metric.key)}
                                    className="w-full text-xs"
                                  >{t('ui.text.reset_to_default')}</Button>
                                </div>
                              </PopoverContent>
                            </Popover>
                          )}
                      <ViewModeButton metricKey={metric.key} />
                      {isOwnProfile && (
                        <>
                          {activeIndex > 0 && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-4 w-4 p-0 hover:bg-transparent"
                              onClick={(e) => {
                                e.stopPropagation();
                                moveChart(activeIndex, "up");
                              }}
                            >
                              <ArrowUp className="w-3 h-3 text-muted-foreground hover:text-foreground" />
                            </Button>
                          )}
                          {activeIndex < activeMetrics.length - 1 && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-4 w-4 p-0 hover:bg-transparent"
                              onClick={(e) => {
                                e.stopPropagation();
                                moveChart(activeIndex, "down");
                              }}
                            >
                              <ArrowDown className="w-3 h-3 text-muted-foreground hover:text-foreground" />
                            </Button>
                          )}
                        </>
                        )}
                      </div>
                      {githubConnection?.last_synced_at && (
                        <div className="text-[10px] text-muted-foreground/60 mt-1">
                          Last synced: {format(new Date(githubConnection.last_synced_at), "MMM d, yyyy")}
                        </div>
                      )}
                    </>
                  ) : metric.key === "mood" || metric.key === "self_appearance_rating" ? (
                    /* Mood and Appearance - rating metrics without editable goals */
                    <>
                      {(isBarMode(metric.key)) && (
                      <>
                      <span className="text-muted-foreground">0{shouldShowUnit(metric.label, metric.unit) ? metric.unit : ''}</span>
                      <div className="flex gap-1">
                        {[0, 1, 2, 3, 4].map((level) => {
                          const customColor = colorPreferences?.[metric.key]?.[level.toString()];
                          return (
                            <div
                              key={level}
                              className={customColor ? "" : getSquareColor(level, false, metric.key)}
                              style={
                                customColor
                                  ? { backgroundColor: customColor, width: "12px", height: "12px", borderRadius: "2px" }
                                  : {
                                      backgroundColor: defaultColors[level as keyof typeof defaultColors],
                                      width: "12px",
                                      height: "12px",
                                      borderRadius: "2px",
                                    }
                              }
                            />
                          );
                        })}
                      </div>
                      {!metric.isRelative && (
                      <span className="text-muted-foreground">
                        {metric.goal}
                        {shouldShowUnit(metric.label, metric.unit) ? metric.unit : ''}
                      </span>
                      )}
                      </>
                      )}
                      {isOwnProfile && (
                        <Popover onOpenChange={(open) => handleColorPickerOpenChange(open, metric.key)}>
                          <PopoverTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-4 w-4 p-0 hover:bg-transparent ml-1">
                              <Palette className="w-3 h-3 text-muted-foreground hover:text-foreground" />
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-64 bg-popover p-3" align="start">
                            <div className="space-y-3">
                              <div className="text-xs font-semibold">{t('ui.text.customize_color')}</div>
                              <div className="space-y-2">
                                <div className="text-xs text-muted-foreground">{t('ui.text.choose_a_hue_for_your_custom_gradient')}</div>
                                <div className="relative">
                                  <div 
                                    className="w-full h-8 rounded mb-2"
                                    style={{
                                      background: 'linear-gradient(to right, hsl(0, 100%, 50%), hsl(60, 100%, 50%), hsl(120, 100%, 50%), hsl(180, 100%, 50%), hsl(240, 100%, 50%), hsl(300, 100%, 50%), hsl(360, 100%, 50%))'
                                    }}
                                  />
                                  <input
                                    type="range"
                                    min="0"
                                    max="360"
                                    value={getCurrentHue(metric.key)}
                                    onChange={(e) => handleColorChange(metric.key, parseInt(e.target.value), getCurrentSaturation(metric.key))}
                                    className="w-full cursor-pointer"
                                  />
                                </div>
                                <div className="relative mt-2">
                                  <div 
                                    className="w-full h-6 rounded mb-2"
                                    style={{
                                      background: `linear-gradient(to right, white, hsl(${getCurrentHue(metric.key)}, 100%, 50%))`
                                    }}
                                  />
                                  <input
                                    type="range"
                                    min="0"
                                    max="100"
                                    value={getCurrentSaturation(metric.key)}
                                    onChange={(e) => handleColorChange(metric.key, getCurrentHue(metric.key), parseInt(e.target.value))}
                                    className="w-full cursor-pointer"
                                  />
                                </div>
                              </div>
                              <div className="flex gap-1">
                                {[0, 1, 2, 3, 4].map((level) => {
                                  const localPalette = getLocalColorPalette(metric.key);
                                  const customColor = localPalette?.[level] || colorPreferences?.[metric.key]?.[level.toString()];
                                  return (
                                    <div
                                      key={level}
                                      className="flex-1 h-8 rounded"
                                      style={{
                                        backgroundColor:
                                          customColor || defaultColors[level as keyof typeof defaultColors],
                                      }}
                                    />
                                  );
                                })}
                              </div>
                              <PresetButtons metricKey={metric.key} />
                              <ScalingButtons metricKey={metric.key} />
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => resetMetricColors(metric.key)}
                                className="w-full text-xs"
                              >{t('ui.text.reset_to_default')}</Button>
                            </div>
                          </PopoverContent>
                        </Popover>
                      )}
                      <ViewModeButton metricKey={metric.key} />
                      {isOwnProfile && (
                        <>
                          {activeIndex > 0 && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-4 w-4 p-0 hover:bg-transparent"
                              onClick={(e) => {
                                e.stopPropagation();
                                moveChart(activeIndex, "up");
                              }}
                            >
                              <ArrowUp className="w-3 h-3 text-muted-foreground hover:text-foreground" />
                            </Button>
                          )}
                          {activeIndex < activeMetrics.length - 1 && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-4 w-4 p-0 hover:bg-transparent"
                              onClick={(e) => {
                                e.stopPropagation();
                                moveChart(activeIndex, "down");
                              }}
                            >
                              <ArrowDown className="w-3 h-3 text-muted-foreground hover:text-foreground" />
                            </Button>
                          )}
                        </>
                      )}
                    </>
                  ) : metric.key === "body_fat_percentage" ? (
                    /* Body fat - closeness to goal is better */
                    <>
                      {isBarMode(metric.key) && (
                      <>
                      <span className="text-muted-foreground">&gt;10% off</span>
                      <div className="flex gap-1">
                        {[0, 1, 2, 3, 4].map((level) => {
                          const customColor = colorPreferences?.[metric.key]?.[level.toString()];
                          return (
                            <div
                              key={level}
                              className={customColor ? "" : getSquareColor(level, false, metric.key)}
                              style={
                                customColor
                                  ? { backgroundColor: customColor, width: "12px", height: "12px", borderRadius: "2px" }
                                  : {
                                      backgroundColor: defaultColors[level as keyof typeof defaultColors],
                                      width: "12px",
                                      height: "12px",
                                      borderRadius: "2px",
                                    }
                              }
                            />
                          );
                        })}
                      </div>
                      </>
                      )}
                      <div className="flex items-center gap-1">
                        {isBarMode(metric.key) && (
                        <>
                        <span className="text-muted-foreground">&lt;1% off</span>
                        {isOwnProfile && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-4 w-4 p-0 hover:bg-transparent"
                            onClick={() => handleEditGoal(metric.key, metric.goal, metric.key === "calories" ? userGoals?.calories_inverse !== false : undefined)}
                          >
                            <Pencil className="w-3 h-3 text-muted-foreground hover:text-foreground" />
                          </Button>
                        )}
                        </>
                        )}
                        {isOwnProfile && (
                          <Popover onOpenChange={(open) => handleColorPickerOpenChange(open, metric.key)}>
                            <PopoverTrigger asChild>
                              <Button variant="ghost" size="sm" className="h-4 w-4 p-0 hover:bg-transparent">
                                <Palette className="w-3 h-3 text-muted-foreground hover:text-foreground" />
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-64 bg-popover p-3" align="start">
                              <div className="space-y-3">
                                <div className="text-xs font-semibold">{t('ui.text.customize_color')}</div>
                                <div className="space-y-2">
                                  <div className="text-xs text-muted-foreground">{t('ui.text.choose_a_hue_for_your_custom_gradient')}</div>
                                  <div className="relative">
                                    <div 
                                      className="w-full h-8 rounded mb-2"
                                      style={{
                                        background: 'linear-gradient(to right, hsl(0, 100%, 50%), hsl(60, 100%, 50%), hsl(120, 100%, 50%), hsl(180, 100%, 50%), hsl(240, 100%, 50%), hsl(300, 100%, 50%), hsl(360, 100%, 50%))'
                                      }}
                                    />
                                    <input
                                      type="range"
                                      min="0"
                                      max="360"
                                      value={getCurrentHue(metric.key)}
                                      onChange={(e) => handleColorChange(metric.key, parseInt(e.target.value), getCurrentSaturation(metric.key))}
                                      className="w-full cursor-pointer"
                                    />
                                  </div>
                                  <div className="relative mt-2">
                                    <div 
                                      className="w-full h-6 rounded mb-2"
                                      style={{
                                        background: `linear-gradient(to right, white, hsl(${getCurrentHue(metric.key)}, 100%, 50%))`
                                      }}
                                    />
                                    <input
                                      type="range"
                                      min="0"
                                      max="100"
                                      value={getCurrentSaturation(metric.key)}
                                      onChange={(e) => handleColorChange(metric.key, getCurrentHue(metric.key), parseInt(e.target.value))}
                                      className="w-full cursor-pointer"
                                    />
                                  </div>
                                </div>
                                <div className="flex gap-1">
                                  {[0, 1, 2, 3, 4].map((level) => {
                                    const localPalette = getLocalColorPalette(metric.key);
                                    const customColor = localPalette?.[level] || colorPreferences?.[metric.key]?.[level.toString()];
                                    return (
                                      <div
                                        key={level}
                                        className="flex-1 h-8 rounded"
                                        style={{
                                          backgroundColor:
                                            customColor || defaultColors[level as keyof typeof defaultColors],
                                        }}
                                      />
                                    );
                                  })}
                                </div>
                                <PresetButtons metricKey={metric.key} />
                                <ScalingButtons metricKey={metric.key} />
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => resetMetricColors(metric.key)}
                                  className="w-full text-xs"
                                >{t('ui.text.reset_to_default')}</Button>
                              </div>
                            </PopoverContent>
                          </Popover>
                        )}
                        <ViewModeButton metricKey={metric.key} />
                        {isOwnProfile && (
                          <>
                            {activeIndex > 0 && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-4 w-4 p-0 hover:bg-transparent"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  moveChart(activeIndex, "up");
                                }}
                              >
                                <ArrowUp className="w-3 h-3 text-muted-foreground hover:text-foreground" />
                              </Button>
                            )}
                            {activeIndex < activeMetrics.length - 1 && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-4 w-4 p-0 hover:bg-transparent"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  moveChart(activeIndex, "down");
                                }}
                              >
                                <ArrowDown className="w-3 h-3 text-muted-foreground hover:text-foreground" />
                              </Button>
                            )}
                          </>
                        )}
                      </div>
                    </>
                  ) : (
                    /* Normal metrics - higher is better */
                    <>
                      {(isBarMode(metric.key)) && (
                      <>
                      <span className="text-muted-foreground">0{shouldShowUnit(metric.label, metric.unit) ? metric.unit : ''}</span>
                      <div className="flex gap-1">
                        {[0, 1, 2, 3, 4].map((level) => {
                          const customColor = colorPreferences?.[metric.key]?.[level.toString()];
                          return (
                            <div
                              key={level}
                              className={customColor ? "" : getSquareColor(level, false, metric.key)}
                              style={
                                customColor
                                  ? { backgroundColor: customColor, width: "12px", height: "12px", borderRadius: "2px" }
                                  : {
                                      backgroundColor: defaultColors[level as keyof typeof defaultColors],
                                      width: "12px",
                                      height: "12px",
                                      borderRadius: "2px",
                                    }
                              }
                            />
                          );
                        })}
                      </div>
                      </>
                      )}
                      <div className="flex items-center gap-2 justify-between">
                        <div className="flex items-center gap-1">
                          {!metric.isRelative && (isBarMode(metric.key)) && (
                          <>
                          <span className="text-muted-foreground">
                            {metric.goal}
                            {shouldShowUnit(metric.label, metric.unit) ? metric.unit : ''}
                          </span>
                          {isOwnProfile && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-4 w-4 p-0 hover:bg-transparent"
                              onClick={() => handleEditGoal(metric.key, metric.goal, metric.key === "calories" ? userGoals?.calories_inverse !== false : undefined)}
                            >
                              <Pencil className="w-3 h-3 text-muted-foreground hover:text-foreground" />
                            </Button>
                          )}
                          </>
                          )}
                          {isOwnProfile && (
                            <Popover onOpenChange={(open) => handleColorPickerOpenChange(open, metric.key)}>

                            <PopoverTrigger asChild>
                              <Button variant="ghost" size="sm" className="h-4 w-4 p-0 hover:bg-transparent">
                                <Palette className="w-3 h-3 text-muted-foreground hover:text-foreground" />
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-64 bg-popover p-3" align="start">
                              <div className="space-y-3">
                                <div className="text-xs font-semibold">{t('ui.text.customize_color')}</div>
                                <div className="space-y-2">
                                  <div className="text-xs text-muted-foreground">{t('ui.text.choose_a_hue_for_your_custom_gradient')}</div>
                                  <div className="relative">
                                    <div 
                                      className="w-full h-8 rounded mb-2"
                                      style={{
                                        background: 'linear-gradient(to right, hsl(0, 100%, 50%), hsl(60, 100%, 50%), hsl(120, 100%, 50%), hsl(180, 100%, 50%), hsl(240, 100%, 50%), hsl(300, 100%, 50%), hsl(360, 100%, 50%))'
                                      }}
                                    />
                                    <input
                                      type="range"
                                      min="0"
                                      max="360"
                                      value={getCurrentHue(metric.key)}
                                      onChange={(e) => handleColorChange(metric.key, parseInt(e.target.value), getCurrentSaturation(metric.key))}
                                      className="w-full cursor-pointer"
                                    />
                                  </div>
                                  <div className="relative mt-2">
                                    <div 
                                      className="w-full h-6 rounded mb-2"
                                      style={{
                                        background: `linear-gradient(to right, white, hsl(${getCurrentHue(metric.key)}, 100%, 50%))`
                                      }}
                                    />
                                    <input
                                      type="range"
                                      min="0"
                                      max="100"
                                      value={getCurrentSaturation(metric.key)}
                                      onChange={(e) => handleColorChange(metric.key, getCurrentHue(metric.key), parseInt(e.target.value))}
                                      className="w-full cursor-pointer"
                                    />
                                  </div>
                                </div>
                                <div className="flex gap-1">
                                  {[0, 1, 2, 3, 4].map((level) => {
                                    const localPalette = getLocalColorPalette(metric.key);
                                    const customColor = localPalette?.[level] || colorPreferences?.[metric.key]?.[level.toString()];
                                    return (
                                      <div
                                        key={level}
                                        className="flex-1 h-8 rounded"
                                        style={{
                                          backgroundColor:
                                            customColor || defaultColors[level as keyof typeof defaultColors],
                                        }}
                                      />
                                    );
                                  })}
                                </div>
                                <PresetButtons metricKey={metric.key} />
                                <ScalingButtons metricKey={metric.key} />
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => resetMetricColors(metric.key)}
                                  className="w-full text-xs"
                                >{t('ui.text.reset_to_default')}</Button>
                              </div>
                            </PopoverContent>
                          </Popover>
                        )}
                        <ViewModeButton metricKey={metric.key} />
                        {isOwnProfile && (
                          <>
                            {activeIndex > 0 && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-4 w-4 p-0 hover:bg-transparent"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  moveChart(activeIndex, "up");
                                }}
                              >
                                <ArrowUp className="w-3 h-3 text-muted-foreground hover:text-foreground" />
                              </Button>
                            )}
                            {activeIndex < activeMetrics.length - 1 && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-4 w-4 p-0 hover:bg-transparent"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  moveChart(activeIndex, "down");
                                }}
                              >
                                <ArrowDown className="w-3 h-3 text-muted-foreground hover:text-foreground" />
                              </Button>
                            )}
                          </>
                        )}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </>
      )}

      <div className="md:hidden">{filtersSection}</div>

      {/* Goal editing dialog */}
      <Dialog open={!!editingGoal} onOpenChange={(open) => !open && setEditingGoal(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('ui.text.edit_goal')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="goal-value">
                {editingGoal && allMetrics.find((m) => m.key === editingGoal.metricKey)?.label} Goal
              </Label>
              <Input
                id="goal-value"
                type="number"
                value={goalValue}
                onChange={(e) => setGoalValue(e.target.value)}
                placeholder={t('forms.placeholders.enter_goal_value')}
              />
            </div>
            {/* Show "Above is bad" checkbox for calories */}
            {editingGoal?.metricKey === "calories" && (
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="calories-inverse-chart-mobile"
                  checked={editingGoal?.isInverse !== false}
                  onCheckedChange={(checked) => 
                    setEditingGoal(editingGoal ? { ...editingGoal, isInverse: checked === true } : null)
                  }
                />
                <Label htmlFor="calories-inverse-chart-mobile" className="text-sm font-normal cursor-pointer">
                  {t('ui.text.above_is_bad')}
                </Label>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingGoal(null)}>
              Cancel
            </Button>
            <Button onClick={handleSaveGoal} disabled={updateGoal.isPending}>
              Save
            </Button>
          </DialogFooter>
          </DialogContent>
        </Dialog>

      {/* Progress Snap Viewer */}
      <ProgressSnapViewer
        key={selectedSnap?.id || 'closed'}
        snap={selectedSnap}
        onClose={() => {
          setSelectedSnap(null);
          setSameDateSnaps([]);
        }}
        currentUserId={currentViewerId || currentUserId || undefined}
        metricEmoji={selectedSnap ? displayMetrics.find(m => m.key === selectedSnap.metric_name)?.emoji : undefined}
        metricLabel={selectedSnap ? displayMetrics.find(m => m.key === selectedSnap.metric_name)?.label : undefined}
        onDelete={() => {
          queryClient.invalidateQueries({ queryKey: ["progress-snaps"] });
        }}
        sameDateSnaps={sameDateSnaps}
      />

      {/* Daily Entries Modal */}
      <DailyEntriesModal
        open={dailyEntriesModalOpen && selectedDayForEntries !== null}
        onOpenChange={setDailyEntriesModalOpen}
        userId={userId}
        currentUserId={currentViewerId}
        entryDate={selectedDayForEntries?.date || ""}
        metricKey={selectedDayForEntries?.metricKey || ""}
        metricName={selectedDayForEntries?.metricName || ""}
        metricEmoji={selectedDayForEntries?.metricEmoji || ""}
        metricUnit={selectedDayForEntries?.metricUnit || ""}
        isBoolean={selectedDayForEntries?.isBoolean || false}
        isTutorialFake={selectedDayForEntries?.isTutorialFake || false}
      />
    </div>
  );
};

export default ContributionChart;